import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  FolderKanban, CalendarDays, Settings,
  Plus, IndianRupee, TrendingUp, AlertTriangle, ArrowLeft, Clock,
  CheckCircle, Circle, Edit2, Trash2, Save, Loader2,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, FileBarChart, Upload, Download,
  Search, Camera, Share2, FileSpreadsheet, Link2, BarChart3, Activity,
  MoreVertical, Filter, ChevronDown, X, RefreshCw, Eye,
  ReceiptIndianRupee, ListChecks, Users
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import CollectionAnalytics from "@/components/CollectionAnalytics";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_valuebot/artifacts/etd4vs0l_Absolin_logo_Blue-HD.png";

const REVENUE_STREAMS = [
  { key: "focus_erp_license_saas", label: "Focus ERP License SaaS", color: "#0066CC" },
  { key: "erp_implementation", label: "ERP Implementation", color: "#2563EB" },
  { key: "app_development", label: "App Development", color: "#7C3AED" },
  { key: "cloud_services", label: "Cloud Services", color: "#D97706" },
  { key: "amc_erp", label: "AMC - ERP", color: "#059669" },
  { key: "amc_web", label: "AMC - Web", color: "#0891B2" },
  { key: "absolin_products", label: "ABSOLIN Products", color: "#DC2626" },
  { key: "ai_consulting_services", label: "AI Consulting Services", color: "#8B5CF6" },
  { key: "others", label: "Others", color: "#64748B" },
  // Legacy aliases — keep for backwards compatibility in display only
  { key: "project_deliverables", label: "Project Deliverables (legacy)", color: "#94A3B8", legacy: true },
  { key: "license_revenue", label: "License Revenue (legacy)", color: "#94A3B8", legacy: true },
  { key: "amc", label: "AMC (legacy)", color: "#94A3B8", legacy: true },
];

const STATUS_MAP = {
  planned: { label: "Planned", color: "bg-blue-100 text-blue-700", icon: Clock },
  collected: { label: "Collected", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle },
  partial: { label: "Partial", color: "bg-amber-100 text-amber-700", icon: Circle },
  overdue: { label: "Overdue", color: "bg-red-100 text-red-700", icon: AlertTriangle },
};

const fmt = (amt) => {
  if (!amt && amt !== 0) return "0";
  // Always render in Lakhs (₹L). Examples: 12500 -> 0.13 L, 250000 -> 2.50 L, 25000000 -> 250 L
  const lakhs = amt / 100000;
  if (lakhs >= 100) return `${Math.round(lakhs).toLocaleString("en-IN")} L`;
  if (lakhs >= 10) return `${lakhs.toFixed(1)} L`;
  return `${lakhs.toFixed(2)} L`;
};

// Indian-format full-rupee display: 65000 -> "₹65,000", 426000 -> "₹4,26,000"
const fmtFull = (amt) => {
  if (!amt && amt !== 0) return "₹0";
  return `₹${Math.round(amt).toLocaleString("en-IN")}`;
};

// Strip non-digits & leading zeros so user-facing field never shows "02323"
const sanitizeNumeric = (raw) => {
  if (raw === "" || raw == null) return "";
  const digits = String(raw).replace(/\D/g, "").replace(/^0+(?=\d)/, "");
  return digits;
};

// ===== Project Search Dropdown =====
function ProjectSearch({ projects, value, onChange, placeholder = "Search project..." }) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = projects.find(p => p.id === value);
  const filtered = projects.filter(p => p.name.toLowerCase().includes(query.toLowerCase()));

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input className="pl-9 text-sm" placeholder={placeholder} value={open ? query : (selected?.name || "")}
          onFocus={() => { setOpen(true); setQuery(""); }}
          onChange={e => { setQuery(e.target.value); setOpen(true); }} />
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.length === 0 ? <p className="p-3 text-xs text-slate-400">No projects found</p> : filtered.map(p => (
            <button key={p.id} onClick={() => { onChange(p.id); setQuery(""); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm flex items-center justify-between">
              <span className="font-medium text-slate-800">{p.name}</span>
              <span className="text-[10px] text-slate-400">Due: {fmt(p.outstanding || 0)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CollectionPlanningPage() {
  const [activeTab, setActiveTab] = useState("analytics");
  const [userData, setUserData] = useState(() => { try { return JSON.parse(localStorage.getItem("user_data") || "{}"); } catch { return {}; } });
  const [projects, setProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState("");
  const [projectTypeFilter, setProjectTypeFilter] = useState([]); // selected keys
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [showProjectDialog, setShowProjectDialog] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectForm, setProjectForm] = useState({ name: "", project_type: "focus_erp_license_saas", order_date: "", start_date: "", project_amount: 0, notes: "", purchase_order_url: "" });
  const [saving, setSaving] = useState(false);

  // Weekly state
  const [weekStart, setWeekStart] = useState(() => { const n = new Date(); const m = new Date(n); m.setDate(n.getDate() - n.getDay() + 1); return m.toISOString().split("T")[0]; });
  const [weeklyPlan, setWeeklyPlan] = useState(null);
  const [showEntryDialog, setShowEntryDialog] = useState(false);
  const [entryForm, setEntryForm] = useState({ project_id: "", date: "", planned_amount: 0, notes: "" });
  const [weeklyView, setWeeklyView] = useState("week"); // 'week' or 'list'
  const [allEntries, setAllEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entriesPage, setEntriesPage] = useState(1);
  const [entriesPageSize, setEntriesPageSize] = useState(25);

  // Daily / Receipts
  const [dailyDate, setDailyDate] = useState(new Date().toISOString().split("T")[0]);
  const [receipts, setReceipts] = useState([]);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [receiptForm, setReceiptForm] = useState({ project_id: "", customer_id: "", receipt_date: "", collection_amount: 0, bank_reference: "", notes: "" });
  // Daily view toggle: 'day' (single date — original) or 'list' (all receipts with filters)
  const [dailyView, setDailyView] = useState("day");
  const [listReceipts, setListReceipts] = useState([]);
  const [listFilters, setListFilters] = useState(() => {
    const now = new Date();
    const mon = new Date(now); mon.setDate(now.getDate() - ((now.getDay() + 6) % 7));
    const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
    return { start: mon.toISOString().split("T")[0], end: sun.toISOString().split("T")[0], project_id: "", q: "" };
  });
  const [listLoading, setListLoading] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(25);

  // Bank Statement
  const [bankLines, setBankLines] = useState([]);
  const [bankMappings, setBankMappings] = useState({});
  const [showBankDialog, setShowBankDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [importResult, setImportResult] = useState(null); // { created, skipped, errors }
  const [receiptCustomers, setReceiptCustomers] = useState([]);

  // Snapshot
  const [snapshotData, setSnapshotData] = useState(null);
  const [showSnapshot, setShowSnapshot] = useState(false);

  const [targets, setTargets] = useState({ daily_target: 250000, weekly_target: 1250000, monthly_target: 5000000, annual_target: 60000000 });

  // FY Targets
  const [fyTargets, setFyTargets] = useState([]);
  const [fyForm, setFyForm] = useState({ fy_start_year: new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1, daily_target: 0, weekly_target: 0, monthly_target: 0, annual_target: 0 });
  const [editingFy, setEditingFy] = useState(null);

  const loadFyTargets = useCallback(async () => {
    try { const r = await api.get("/collections/fy-targets"); setFyTargets(r.data || []); } catch {}
  }, []);
  const handleSaveFy = async () => {
    setSaving(true);
    try {
      if (editingFy) {
        await api.put(`/collections/fy-targets/${editingFy.id}`, fyForm);
        toast.success("FY target updated");
      } else {
        await api.post("/collections/fy-targets", { ...fyForm, fy_label: `FY ${fyForm.fy_start_year}-${String(fyForm.fy_start_year + 1).slice(-2)}` });
        toast.success("FY target added");
      }
      setEditingFy(null);
      setFyForm({ fy_start_year: new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1, daily_target: 0, weekly_target: 0, monthly_target: 0, annual_target: 0 });
      loadFyTargets();
      setAnalytics(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  const handleDeleteFy = async (id) => {
    if (!window.confirm("Delete this FY target?")) return;
    try { await api.delete(`/collections/fy-targets/${id}`); toast.success("Deleted"); loadFyTargets(); setAnalytics(null); }
    catch { toast.error("Delete failed"); }
  };
  const handleEditFy = (fy) => {
    setEditingFy(fy);
    setFyForm({ fy_start_year: fy.fy_start_year, daily_target: fy.daily_target, weekly_target: fy.weekly_target, monthly_target: fy.monthly_target, annual_target: fy.annual_target });
  };

  // Data Eraser (bulk-delete receipts by date range)
  const [eraseRange, setEraseRange] = useState({ start: "", end: "" });
  const [erasePreview, setErasePreview] = useState(null); // { count, total_amount }
  const [erasePreviewLoading, setErasePreviewLoading] = useState(false);
  const [eraseConfirmOpen, setEraseConfirmOpen] = useState(false);
  const [erasing, setErasing] = useState(false);
  const erasePreviewRef = useRef(null);

  // Unambiguous date formatter for display (e.g. "1 Mar 2026") — avoids US/UK MM/DD vs DD/MM confusion
  const fmtDateLong = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(`${iso}T00:00:00`);
      if (isNaN(d)) return iso;
      return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
    } catch { return iso; }
  };

  const applyEraserQuickRange = (key) => {
    const now = new Date();
    const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    let start, end;
    if (key === "currentFY") {
      // Indian FY Apr-Mar
      const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
      start = iso(new Date(y, 3, 1));
      end = iso(new Date(y + 1, 2, 31));
    } else if (key === "currentMonth") {
      start = iso(new Date(now.getFullYear(), now.getMonth(), 1));
      end = iso(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    } else if (key === "last90") {
      const s = new Date(); s.setDate(s.getDate() - 89);
      start = iso(s); end = iso(now);
    } else if (key === "allTime") {
      start = "1970-01-01"; end = "2099-12-31";
    }
    setEraseRange({ start, end });
    setErasePreview(null);
  };

  const handleErasePreview = async () => {
    if (!eraseRange.start || !eraseRange.end) { toast.error("Select both start and end dates"); return; }
    if (eraseRange.start > eraseRange.end) { toast.error("Start date cannot be after end date"); return; }
    setErasePreviewLoading(true); setErasePreview(null);
    try {
      const r = await api.get(`/collections/receipts/bulk-delete-preview?start_date=${eraseRange.start}&end_date=${eraseRange.end}`);
      setErasePreview(r.data);
      // Scroll the preview panel into view so the result is visible below the button
      setTimeout(() => {
        erasePreviewRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 60);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    setErasePreviewLoading(false);
  };

  const handleEraseConfirm = async () => {
    setErasing(true);
    try {
      const r = await api.delete(`/collections/receipts/bulk?start_date=${eraseRange.start}&end_date=${eraseRange.end}`);
      toast.success(`Erased ${r.data.deleted} receipt${r.data.deleted === 1 ? "" : "s"}`);
      setErasePreview(null);
      setEraseRange({ start: "", end: "" });
      setEraseConfirmOpen(false);
      // Refresh any open lists / analytics
      setAnalytics(null);
      if (activeTab === "daily") { loadReceipts(); loadListReceipts(); }
    } catch (e) { toast.error(e.response?.data?.detail || "Erase failed"); }
    setErasing(false);
  };

  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [fyComparison, setFyComparison] = useState(null);
  const [yoyData, setYoyData] = useState(null);
  const [selectedFys, setSelectedFys] = useState([]); // start years
  const [aiInsights, setAiInsights] = useState("");
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [loadingAi, setLoadingAi] = useState(false);

  const loadDashboard = useCallback(() => { setAnalytics(null); }, []);
  const loadProjects = useCallback(async () => { try { const r = await api.get("/collections/projects"); setProjects(r.data || []); } catch {} }, []);
  const loadReceiptCustomers = useCallback(async () => { try { const r = await api.get("/collections/customers"); setReceiptCustomers(r.data || []); } catch {} }, []);
  const loadTargets = useCallback(async () => { try { const r = await api.get("/collections/targets"); setTargets(r.data); } catch {} }, []);
  const loadAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const [dashRes, yoyRes] = await Promise.all([
        api.get("/collections/analytics/dashboard"),
        api.get("/collections/analytics/yoy-comparison"),
      ]);
      setAnalytics(dashRes.data);
      setYoyData(yoyRes.data);
    } catch { toast.error("Failed to load analytics"); }
    setLoadingAnalytics(false);
  }, []);
  const loadFyComparison = useCallback(async (fys) => {
    if (!fys || fys.length === 0) { setFyComparison(null); return; }
    setLoadingAnalytics(true);
    try { const r = await api.get(`/collections/analytics/fy-comparison?fys=${fys.join(",")}`); setFyComparison(r.data); }
    catch { toast.error("Failed to load FY comparison"); }
    setLoadingAnalytics(false);
  }, []);
  const generateAiInsights = useCallback(async () => {
    setLoadingAi(true); setAiInsights("");
    try { const r = await api.post("/collections/analytics/ai-insights"); setAiInsights(r.data.insights); }
    catch (e) { toast.error(e.response?.data?.detail || "AI insights failed — check Admin > AI Settings"); }
    setLoadingAi(false);
  }, []);

  useEffect(() => { (async () => { setLoading(true); await Promise.all([loadDashboard(), loadProjects(), loadTargets(), loadReceiptCustomers()]); setLoading(false); })(); }, []); // eslint-disable-line

  const loadWeeklyPlan = useCallback(async () => { try { const r = await api.get(`/collections/planner/weekly?week_start=${weekStart}`); setWeeklyPlan(r.data); } catch {} }, [weekStart]);
  const loadAllEntries = useCallback(async () => {
    setEntriesLoading(true);
    try { const r = await api.get("/collections/entries"); setAllEntries(r.data || []); }
    catch { toast.error("Failed to load entries"); }
    setEntriesLoading(false);
  }, []);
  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this planned entry?")) return;
    try { await api.delete(`/collections/entries/${id}`); toast.success("Deleted"); loadAllEntries(); loadWeeklyPlan(); }
    catch { toast.error("Delete failed"); }
  };
  const editEntry = (entry) => {
    setEntryForm({ id: entry.id, project_id: entry.project_id, date: entry.date, planned_amount: entry.planned_amount || 0, notes: entry.notes || "" });
    setShowEntryDialog(true);
  };
  const loadReceipts = useCallback(async () => {
    try { const r = await api.get(`/collections/receipts?start_date=${dailyDate}&end_date=${dailyDate}`); setReceipts(r.data || []); } catch {}
  }, [dailyDate]);
  const loadListReceipts = useCallback(async () => {
    setListLoading(true);
    try {
      const params = new URLSearchParams();
      if (listFilters.start) params.set("start_date", listFilters.start);
      if (listFilters.end) params.set("end_date", listFilters.end);
      if (listFilters.project_id) params.set("project_id", listFilters.project_id);
      const r = await api.get(`/collections/receipts?${params.toString()}`);
      setListReceipts(r.data || []);
    } catch { toast.error("Failed to load receipts"); }
    setListLoading(false);
  }, [listFilters.start, listFilters.end, listFilters.project_id]);

  // Reset pagination when dataset / filters change
  useEffect(() => { setEntriesPage(1); }, [allEntries.length]);
  useEffect(() => { setListPage(1); }, [listReceipts.length, listFilters.q]);

  useEffect(() => { if (activeTab === "weekly" && weeklyView === "week") loadWeeklyPlan(); }, [activeTab, weeklyView, loadWeeklyPlan]);
  useEffect(() => { if (activeTab === "weekly" && weeklyView === "list") loadAllEntries(); }, [activeTab, weeklyView, loadAllEntries]);
  useEffect(() => { if (activeTab === "daily") loadReceipts(); }, [activeTab, loadReceipts]);
  useEffect(() => { if (activeTab === "daily") loadListReceipts(); }, [activeTab, loadListReceipts]);
  useEffect(() => { if (activeTab === "analytics" && !analytics) loadAnalytics(); }, [activeTab, analytics, loadAnalytics]);
  useEffect(() => { if (activeTab === "analytics" && fyTargets.length === 0) loadFyTargets(); }, [activeTab, fyTargets.length, loadFyTargets]);
  useEffect(() => { if (activeTab === "settings") loadFyTargets(); }, [activeTab, loadFyTargets]);

  const fileInputRef = useRef(null);
  const bankFileRef = useRef(null);
  const handleProjectsImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return; e.target.value = "";
    const fd = new FormData(); fd.append("file", file);
    setImporting(true);
    try {
      const r = await api.post("/collections/projects/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await loadProjects();
      setImportResult(r.data);
    } catch (err) {
      setImportResult({ created: 0, skipped: 0, errors: [err.response?.data?.detail || "Import failed"] });
    }
    finally { setImporting(false); }
  };
  const downloadProjectsExport = async () => {
    try {
      const res = await api.get("/collections/projects/export", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `projects_export_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Projects exported");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Export failed — please retry");
    }
  };
  const downloadProjectsTemplate = () => { window.open(`${api.defaults.baseURL}/collections/templates/projects`, "_blank"); };

  const filteredProjects = projects.filter(p => {
    if (projectSearch && !p.name.toLowerCase().includes(projectSearch.toLowerCase())) return false;
    if (projectTypeFilter.length > 0 && !projectTypeFilter.includes(p.project_type)) return false;
    return true;
  });

  // Project CRUD
  const handleSaveProject = async () => {
    if (!projectForm.name) { toast.error("Project name required"); return; }
    setSaving(true);
    try {
      if (editingProject) { await api.put(`/collections/projects/${editingProject.id}`, projectForm); toast.success("Updated"); }
      else { await api.post("/collections/projects", projectForm); toast.success("Created"); }
      setShowProjectDialog(false); setEditingProject(null); loadProjects(); loadDashboard();
    } catch (e) { toast.error("Failed"); }
    setSaving(false);
  };
  const handleDeleteProject = async (id) => {
    if (!window.confirm("Delete project and all its data?")) return;
    try { await api.delete(`/collections/projects/${id}`); toast.success("Deleted"); loadProjects(); loadDashboard(); } catch { toast.error("Failed"); }
  };

  // Weekly Entry
  const handleSaveEntry = async () => {
    if (!entryForm.project_id || !entryForm.date || !entryForm.planned_amount) { toast.error("Fill all fields"); return; }
    setSaving(true);
    try {
      if (entryForm.id) {
        const { id, ...payload } = entryForm;
        await api.put(`/collections/entries/${id}`, payload);
        toast.success("Entry updated");
      } else {
        await api.post("/collections/entries", entryForm);
        toast.success("Entry added");
      }
      setShowEntryDialog(false); loadWeeklyPlan(); loadDashboard();
      if (weeklyView === "list") loadAllEntries();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save planned entry"); }
    setSaving(false);
  };

  // Receipt
  const handleSaveReceipt = async () => {
    if (!receiptForm.project_id || !receiptForm.receipt_date || !receiptForm.collection_amount) { toast.error("Fill all fields"); return; }
    setSaving(true);
    try {
      await api.post("/collections/receipts", receiptForm);
      toast.success("Receipt recorded"); setShowReceiptDialog(false); loadReceipts(); loadListReceipts(); loadDashboard(); loadProjects();
    } catch (e) { toast.error("Failed"); }
    setSaving(false);
  };

  const deleteReceipt = async (id) => {
    try { await api.delete(`/collections/receipts/${id}`); toast.success("Deleted"); loadReceipts(); loadListReceipts(); loadDashboard(); } catch { toast.error("Failed"); }
  };

  // Bank statement upload
  const handleBankUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = "";
    const fd = new FormData(); fd.append("file", file);
    setImporting(true);
    try {
      const r = await api.post("/collections/bank-statement/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const lines = r.data.lines || [];
      if (lines.length === 0) {
        toast.error("No valid lines found in file");
        return;
      }
      // Pre-fill mappings with auto-suggested projects
      const initialMappings = {};
      lines.forEach(l => { if (l.suggested_project_id) initialMappings[l.id] = l.suggested_project_id; });
      setBankLines(lines);
      setBankMappings(initialMappings);
      setShowBankDialog(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed — check file format"); }
    finally { setImporting(false); }
  };

  const handleBankMap = async () => {
    const mappings = Object.entries(bankMappings).filter(([_, pid]) => pid).map(([lid, pid]) => ({ line_id: lid, project_id: pid }));
    if (mappings.length === 0) { toast.error("Map at least one line"); return; }
    setSaving(true);
    try {
      const r = await api.post("/collections/bank-statement/map", mappings);
      setShowBankDialog(false);
      await Promise.all([loadReceipts(), loadProjects()]);
      const unmapped = bankLines.length - mappings.length;
      const errors = r.data.errors || [];
      setTimeout(() => setImportResult({ created: r.data.created, skipped: unmapped, errors: errors.length > 0 ? errors : [] }), 200);
      if (r.data.created > 0) toast.success(`${r.data.created} receipt(s) created`);
      else toast.error(`No receipts created. ${errors.length > 0 ? errors[0] : "Check if lines exist."}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Mapping failed"); }
    setSaving(false);
  };

  // Historical import
  const handleHistoricalImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = "";
    const fd = new FormData(); fd.append("file", file);
    setImporting(true);
    try {
      const r = await api.post("/collections/receipts/import-historical", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await Promise.all([loadReceipts(), loadProjects()]);
      setShowImportDialog(false);
      // Small delay to let the import dialog close before opening result dialog
      setTimeout(() => setImportResult(r.data), 200);
    } catch (err) {
      setShowImportDialog(false);
      setTimeout(() => setImportResult({ created: 0, skipped: 0, errors: [err.response?.data?.detail || "Import failed — check file format"] }), 200);
    }
    finally { setImporting(false); }
  };

  // Weekly Plan Template Download (auth-protected — must use api blob)
  const downloadWeeklyTemplate = async () => {
    try {
      const r = await api.get("/collections/templates/weekly-plan", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "weekly_plan_template.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Template downloaded — fill Date & Planning Amount, then upload back");
    } catch { toast.error("Template download failed"); }
  };

  const handleWeeklyImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    e.target.value = "";
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/collections/planner/weekly/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.created} entries. Skipped: ${r.data.skipped}`);
      if (r.data.errors?.length > 0) {
        r.data.errors.slice(0, 3).forEach(err => toast.error(err));
      }
      loadWeeklyPlan(); loadDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || "Weekly import failed"); }
  };

  // Snapshot
  const loadSnapshot = async (projectId) => {
    try { const r = await api.get(`/collections/projects/${projectId}/snapshot`); setSnapshotData(r.data); setShowSnapshot(true); } catch { toast.error("Failed"); }
  };

  const handleSaveTargets = async () => {
    setSaving(true);
    try { await api.put("/collections/targets", targets); toast.success("Targets saved"); loadDashboard(); } catch { toast.error("Failed"); }
    setSaving(false);
  };

  const TABS = [
    { id: "analytics", label: "Analytics", icon: Activity, adminOnly: true },
    { id: "projects", label: "Projects", icon: FolderKanban },
    { id: "customers", label: "Customers", icon: Users },
    { id: "weekly", label: "Weekly Plan", icon: CalendarDays },
    { id: "daily", label: "Receipts", icon: ReceiptIndianRupee },
    { id: "reports", label: "Reports", icon: FileBarChart },
    { id: "settings", label: "Settings", icon: Settings, adminOnly: true },
  ].filter(t => !t.adminOnly || userData.role === 'admin');

  const prevWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d.toISOString().split("T")[0]); };
  const nextWeek = () => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d.toISOString().split("T")[0]); };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="collection-planning">
      {/* Sidebar */}
      <aside className="w-56 bg-white border-r border-slate-200 fixed h-full z-30 hidden lg:block">
        <div className="p-4 border-b border-slate-100">
          <Link to="/"><img src={LOGO_URL} alt="Absolin" className="h-8 object-contain" /></Link>
          <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-wider font-semibold">Collection Planning</p>
        </div>
        <nav className="p-3 space-y-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${activeTab === t.id ? "bg-[#0066CC] text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
        <div className="absolute bottom-4 left-0 right-0 px-4">
          <Link to="/admin-portal" className="flex items-center gap-2 text-xs text-slate-400 hover:text-[#0066CC]"><ArrowLeft className="w-3.5 h-3.5" /> Back to Admin</Link>
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 bg-white border-b z-30 px-4 py-3">
        <div className="flex items-center gap-2"><img src={LOGO_URL} alt="Absolin" className="h-6" /><span className="text-xs font-semibold text-slate-500">Collections</span></div>
        <div className="flex gap-1 mt-2 overflow-x-auto pb-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${activeTab === t.id ? "bg-[#0066CC] text-white" : "bg-slate-100 text-slate-600"}`}><t.icon className="w-3.5 h-3.5" /> {t.label}</button>
          ))}
        </div>
      </div>

      {/* Main */}
      <main className="flex-1 lg:ml-56 pt-24 lg:pt-0">
        <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">

          {/* PROJECTS */}
          {activeTab === "projects" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Project Master</h1>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button className="bg-[#0066CC] hover:bg-[#0052A3] gap-2" data-testid="projects-actions-btn">
                      Actions <ChevronDown className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    <DropdownMenuItem onClick={() => { setEditingProject(null); setProjectForm({ name: "", project_type: "focus_erp_license_saas", order_date: "", start_date: "", project_amount: 0, notes: "", purchase_order_url: "" }); setShowProjectDialog(true); }} data-testid="action-add-project">
                      <Plus className="w-4 h-4 mr-2" /> Add Project
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setShowBulkEdit(true)} data-testid="action-bulk-edit-projects">
                      <ListChecks className="w-4 h-4 mr-2" /> Bulk Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={downloadProjectsExport} data-testid="action-export-projects">
                      <Download className="w-4 h-4 mr-2" /> Export Projects
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => fileInputRef.current?.click()} data-testid="action-import-projects">
                      <Upload className="w-4 h-4 mr-2" /> Import Projects
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={downloadProjectsTemplate} data-testid="action-template-projects">
                      <FileSpreadsheet className="w-4 h-4 mr-2" /> Download Template
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden" onChange={handleProjectsImport} />
              </div>

              {/* Importing overlay */}
              {importing && (
                <div className="flex items-center justify-center gap-3 py-8 bg-blue-50 border border-blue-200 rounded-lg">
                  <Loader2 className="w-5 h-5 animate-spin text-[#0066CC]" />
                  <span className="text-sm font-medium text-[#0066CC]">Importing projects... please wait</span>
                </div>
              )}

              {/* Search + Type Filter */}
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input className="pl-9 h-10" placeholder="Search by project name..." value={projectSearch} onChange={(e) => setProjectSearch(e.target.value)} data-testid="project-search-input" />
                  {projectSearch && (
                    <button onClick={() => setProjectSearch("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded">
                      <X className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                  )}
                </div>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-2 h-10" data-testid="project-type-filter-btn">
                      <Filter className="w-3.5 h-3.5" />
                      Type {projectTypeFilter.length > 0 && <span className="ml-1 bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded text-[10px] font-bold">{projectTypeFilter.length}</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2" align="end">
                    <div className="flex items-center justify-between px-2 py-1.5">
                      <p className="text-xs font-semibold text-slate-600">Filter by Type</p>
                      {projectTypeFilter.length > 0 && (
                        <button onClick={() => setProjectTypeFilter([])} className="text-[10px] text-blue-600 hover:underline">Clear</button>
                      )}
                    </div>
                    <div className="max-h-72 overflow-y-auto space-y-0.5 mt-1">
                      {REVENUE_STREAMS.filter(s => !s.legacy).map(s => (
                        <label key={s.key} className="flex items-center gap-2 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer text-sm">
                          <Checkbox
                            checked={projectTypeFilter.includes(s.key)}
                            onCheckedChange={(checked) => {
                              setProjectTypeFilter(prev => checked ? [...prev, s.key] : prev.filter(k => k !== s.key));
                            }}
                            data-testid={`filter-type-${s.key}`}
                          />
                          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                          <span className="flex-1">{s.label}</span>
                        </label>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <p className="text-xs text-slate-500 whitespace-nowrap">{filteredProjects.length} of {projects.length}</p>
              </div>

              <div className="grid gap-3">
                {filteredProjects.map(p => (
                  <Card key={p.id} className="border-slate-200 hover:border-blue-200 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-slate-900 text-sm">{p.name}</h3>
                            <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${p.status === "active" ? "bg-emerald-100 text-emerald-700" : p.status === "completed" ? "bg-slate-100 text-slate-600" : "bg-amber-100 text-amber-700"}`}>{p.status}</span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">{REVENUE_STREAMS.find(s => s.key === p.project_type)?.label || p.project_type}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1">Amount: <strong className="text-slate-700">{fmt(_getProjectAmount(p))}</strong></p>
                        </div>
                        <div className="text-right shrink-0">
                          <p className="text-xs text-slate-400">Collected</p>
                          <p className="text-sm font-bold text-emerald-600">{fmt(p.total_collected || 0)}</p>
                          <p className="text-[10px] text-slate-400">Due: <span className="text-red-600 font-semibold">{fmt(p.outstanding || 0)}</span></p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3 flex-wrap">
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => {
                          setEditingProject(p); setProjectForm({ name: p.name, project_type: p.project_type, order_date: p.order_date || "", start_date: p.start_date || "", project_amount: _getProjectAmount(p), notes: p.notes || "", purchase_order_url: p.purchase_order_url || "" }); setShowProjectDialog(true);
                        }}><Edit2 className="w-3 h-3 mr-1" /> Edit</Button>
                        {p.purchase_order_url && /^https?:\/\//.test(p.purchase_order_url) && (
                          <a href={p.purchase_order_url} target="_blank" rel="noopener noreferrer" data-testid={`project-po-link-${p.id}`}>
                            <Button variant="ghost" size="sm" className="h-7 text-xs text-[#0066CC] hover:bg-blue-50"><Eye className="w-3 h-3 mr-1" /> View PO</Button>
                          </a>
                        )}
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-emerald-600 hover:bg-emerald-50" onClick={() => loadSnapshot(p.id)}><Camera className="w-3 h-3 mr-1" /> Snapshot</Button>
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600 hover:bg-red-50" onClick={() => handleDeleteProject(p.id)}><Trash2 className="w-3 h-3 mr-1" /> Delete</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                {projects.length === 0 && <p className="text-center text-slate-400 py-12">No projects yet.</p>}
                {projects.length > 0 && filteredProjects.length === 0 && <p className="text-center text-slate-400 py-12">No projects match your filters.</p>}
              </div>
            </div>
          )}

          {/* CUSTOMERS */}
          {activeTab === "customers" && (
            <CustomerMasterPanel projects={projects} />
          )}

          {/* WEEKLY PLANNER */}
          {activeTab === "weekly" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Weekly Collection Plan</h1>
                <div className="flex gap-2 flex-wrap items-center">
                  {/* View toggle */}
                  <div className="inline-flex rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid="weekly-view-toggle">
                    <button type="button" onClick={() => setWeeklyView("week")} className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 ${weeklyView === "week" ? "bg-[#0066CC] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="view-week-btn">
                      <CalendarDays className="w-3.5 h-3.5" /> Week View
                    </button>
                    <button type="button" onClick={() => setWeeklyView("list")} className={`px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 ${weeklyView === "list" ? "bg-[#0066CC] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid="view-list-btn">
                      <ListChecks className="w-3.5 h-3.5" /> List View
                    </button>
                  </div>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={downloadWeeklyTemplate} data-testid="weekly-template-btn"><FileSpreadsheet className="w-3.5 h-3.5" /> Template</Button>
                  <label><Button variant="outline" size="sm" className="gap-1.5 cursor-pointer" asChild><span><Upload className="w-3.5 h-3.5" /> Import</span></Button><input type="file" accept=".xlsx" className="hidden" onChange={handleWeeklyImport} data-testid="weekly-import-input" /></label>
                  <Button onClick={() => { setEntryForm({ project_id: "", date: weekStart, planned_amount: 0, notes: "" }); setShowEntryDialog(true); }} className="bg-[#0066CC] hover:bg-[#0052A3] gap-2" data-testid="add-entry-btn"><Plus className="w-4 h-4" /> Add Entry</Button>
                </div>
              </div>
              {weeklyView === "week" && (
                <>
                  <div className="flex items-center gap-3 bg-white rounded-lg border p-3">
                    <Button variant="ghost" size="sm" onClick={prevWeek}><ChevronLeft className="w-4 h-4" /></Button>
                    <div className="flex-1 text-center"><p className="text-sm font-semibold text-slate-800">Week of {new Date(weekStart + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })} - {weeklyPlan?.week_end ? new Date(weeklyPlan.week_end + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : ""}</p></div>
                    <Button variant="ghost" size="sm" onClick={nextWeek}><ChevronRight className="w-4 h-4" /></Button>
                  </div>
                  {weeklyPlan && (
                    <>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="bg-white rounded-lg border p-3 text-center"><p className="text-lg font-bold text-[#0066CC]">{fmt(weeklyPlan.total_planned)}</p><p className="text-[10px] text-slate-500">Planned</p></div>
                        <div className="bg-white rounded-lg border p-3 text-center"><p className="text-lg font-bold text-emerald-600">{fmt(weeklyPlan.total_collected)}</p><p className="text-[10px] text-slate-500">Received <span className="text-[9px] text-slate-400">(from Receipts)</span></p></div>
                        <div className="bg-white rounded-lg border p-3 text-center"><p className={`text-lg font-bold ${weeklyPlan.gap > 0 ? "text-red-600" : "text-emerald-600"}`}>{fmt(Math.abs(weeklyPlan.gap))}</p><p className="text-[10px] text-slate-500">{weeklyPlan.gap > 0 ? "Gap" : "Surplus"}</p></div>
                      </div>
                      <p className="text-[11px] text-slate-500 italic px-1">Tip: actual collections are pulled from the Receipts screen — record them there to update progress here automatically.</p>
                      {weeklyPlan.dates?.map(date => {
                        const entries = weeklyPlan.entries_by_date?.[date] || [];
                        const dayName = new Date(date + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
                        const isToday = date === new Date().toISOString().split("T")[0];
                        return (
                          <div key={date} className={`bg-white rounded-lg border p-3 ${isToday ? "border-[#0066CC] ring-1 ring-[#0066CC]/20" : ""}`}>
                            <div className="flex items-center justify-between mb-2">
                              <p className={`text-xs font-semibold ${isToday ? "text-[#0066CC]" : "text-slate-600"}`}>{dayName} {isToday && <span className="ml-1 text-[10px] bg-blue-100 px-1.5 py-0.5 rounded">TODAY</span>}</p>
                              <p className="text-xs text-slate-400">{fmt(entries.reduce((s, e) => s + (e.planned_amount || 0), 0))} planned</p>
                            </div>
                            {entries.length > 0 ? entries.map(e => {
                              const collected = e.collected_from_receipts || 0;
                              const planned = e.planned_amount || 0;
                              const isFullyCollected = collected >= planned && planned > 0;
                              const isPartial = collected > 0 && collected < planned;
                              const StatusIcon = isFullyCollected ? CheckCircle : (isPartial ? Circle : Clock);
                              const iconColor = isFullyCollected ? "text-emerald-600" : (isPartial ? "text-amber-600" : "text-blue-500");
                              return (
                                <div key={e.id} className="flex items-center justify-between gap-2 py-1.5 px-2 rounded bg-slate-50 text-xs mb-1">
                                  <div className="flex items-center gap-2 min-w-0">
                                    <StatusIcon className={`w-3.5 h-3.5 shrink-0 ${iconColor}`} />
                                    <span className="font-medium text-slate-800 truncate">{e.project_name}</span>
                                  </div>
                                  <div className="flex items-center gap-3 shrink-0">
                                    <div className="text-right">
                                      <span className="font-semibold text-slate-700">{fmt(planned)}</span>
                                      <span className="text-[9px] text-slate-400 ml-1">planned</span>
                                    </div>
                                    <div className="text-right">
                                      <span className={`font-semibold ${collected > 0 ? "text-emerald-600" : "text-slate-300"}`}>{fmt(collected)}</span>
                                      <span className="text-[9px] text-slate-400 ml-1">received</span>
                                    </div>
                                  </div>
                                </div>
                              );
                            }) : <p className="text-[10px] text-slate-300 italic">No entries</p>}
                          </div>
                        );
                      })}
                    </>
                  )}
                </>
              )}

              {/* LIST VIEW — all weekly plan entries */}
              {weeklyView === "list" && (
                <Card data-testid="weekly-list-view">
                  <CardContent className="p-0">
                    <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-slate-800">All Planned Entries</h3>
                        <p className="text-[11px] text-slate-500">{allEntries.length} total — click row to edit, trash to delete</p>
                      </div>
                      <p className="text-xs text-slate-500">Total planned: <strong className="text-[#0066CC]">{fmt(allEntries.reduce((s, e) => s + (e.planned_amount || 0), 0))}</strong></p>
                    </div>
                    {entriesLoading ? (
                      <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
                    ) : allEntries.length === 0 ? (
                      <p className="text-center text-slate-400 py-12">No planned entries yet.</p>
                    ) : (
                      <>
                        <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
                          <table className="w-full text-xs sm:text-sm" data-testid="entries-table">
                            <thead className="bg-slate-50 sticky top-0 z-10 shadow-[0_1px_0_rgba(0,0,0,0.06)]">
                              <tr>
                                <th className="text-left p-3 font-semibold text-slate-600">Date</th>
                                <th className="text-left p-3 font-semibold text-slate-600">Project</th>
                                <th className="text-right p-3 font-semibold text-slate-600">Planned</th>
                                <th className="text-left p-3 font-semibold text-slate-600 hidden md:table-cell">Notes</th>
                                <th className="text-right p-3 font-semibold text-slate-600 w-[110px]">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(() => {
                                const sorted = [...allEntries].sort((a, b) => b.date.localeCompare(a.date));
                                const start = (entriesPage - 1) * entriesPageSize;
                                const slice = sorted.slice(start, start + entriesPageSize);
                                return slice.map(e => (
                                  <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50">
                                    <td className="p-3 whitespace-nowrap text-slate-700">{e.date}</td>
                                    <td className="p-3 font-medium text-slate-800 max-w-[260px] truncate">{e.project_name}</td>
                                    <td className="p-3 text-right font-semibold text-[#0066CC]">{fmt(e.planned_amount)}</td>
                                    <td className="p-3 text-slate-500 text-[11px] hidden md:table-cell max-w-[200px] truncate">{e.notes || "—"}</td>
                                    <td className="p-3 text-right whitespace-nowrap">
                                      <Button variant="ghost" size="sm" className="h-7" onClick={() => editEntry(e)} data-testid={`edit-entry-${e.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                                      <Button variant="ghost" size="sm" className="h-7 text-red-600" onClick={() => deleteEntry(e.id)} data-testid={`delete-entry-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                                    </td>
                                  </tr>
                                ));
                              })()}
                            </tbody>
                          </table>
                        </div>
                        <PaginationBar
                          total={allEntries.length}
                          page={entriesPage}
                          pageSize={entriesPageSize}
                          onPageChange={setEntriesPage}
                          onPageSizeChange={setEntriesPageSize}
                          testId="entries-pagination"
                        />
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* DAILY RECEIPTS */}
          {activeTab === "daily" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Daily Collection Receipts</h1>
                <div className="flex gap-2 flex-wrap items-center">
                                    <Button onClick={() => { setReceiptForm({ project_id: "", customer_id: "", receipt_date: new Date().toISOString().split("T")[0], collection_amount: 0, bank_reference: "", notes: "" }); setShowReceiptDialog(true); }} className="bg-[#0066CC] hover:bg-[#0052A3] gap-2"><Plus className="w-4 h-4" /> New Receipt</Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="icon" className="h-9 w-9" title="Bank actions" data-testid="receipts-bank-menu-btn">
                        <MoreVertical className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      <DropdownMenuItem onClick={() => bankFileRef.current?.click()} data-testid="action-bank-statement">
                        <FileSpreadsheet className="w-4 h-4 mr-2" /> Upload Bank Statement
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild data-testid="action-bank-template">
                        <a href={`${api.defaults.baseURL}/collections/templates/bank-statement`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                          <Download className="w-4 h-4" /> Bank Template
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setShowImportDialog(true)} data-testid="action-historical-import">
                        <Upload className="w-4 h-4 mr-2" /> Historical Import
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <input ref={bankFileRef} type="file" accept=".xlsx" className="hidden" onChange={handleBankUpload} />
                </div>
              </div>

              {/* Importing overlay */}
              {importing && (
                <div className="flex items-center justify-center gap-3 py-8 bg-blue-50 border border-blue-200 rounded-lg">
                  <Loader2 className="w-5 h-5 animate-spin text-[#0066CC]" />
                  <span className="text-sm font-medium text-[#0066CC]">Processing file... please wait</span>
                </div>
              )}

              {/* Filter Card with 4 Quick Chips */}
              <Card className="border-slate-200">
                <CardContent className="p-4 space-y-3">
                  {/* 4 Quick Chips */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mr-1">Quick:</span>
                    {[
                      { id: "today", label: "Today", getRange: () => { const t = new Date().toISOString().split("T")[0]; return { start: t, end: t }; } },
                      { id: "yesterday", label: "Yesterday", getRange: () => { const d = new Date(); d.setDate(d.getDate() - 1); const y = d.toISOString().split("T")[0]; return { start: y, end: y }; } },
                      { id: "current-week", label: "Current Week", getRange: () => { const now = new Date(); const mon = new Date(now); mon.setDate(now.getDate() - ((now.getDay() + 6) % 7)); const sun = new Date(mon); sun.setDate(mon.getDate() + 6); return { start: mon.toISOString().split("T")[0], end: sun.toISOString().split("T")[0] }; } },
                      { id: "last-week", label: "Last Week", getRange: () => { const now = new Date(); const thisMon = new Date(now); thisMon.setDate(now.getDate() - ((now.getDay() + 6) % 7)); const lastMon = new Date(thisMon); lastMon.setDate(thisMon.getDate() - 7); const lastSun = new Date(lastMon); lastSun.setDate(lastMon.getDate() + 6); return { start: lastMon.toISOString().split("T")[0], end: lastSun.toISOString().split("T")[0] }; } },
                    ].map(chip => (
                      <button
                        key={chip.id}
                        data-testid={`receipts-quick-${chip.id}`}
                        onClick={() => { const r = chip.getRange(); setListFilters(f => ({ ...f, start: r.start, end: r.end })); setTimeout(() => loadListReceipts(), 30); }}
                        className={`px-3 py-1.5 text-xs font-medium rounded-full border transition ${listFilters.start === chip.getRange().start && listFilters.end === chip.getRange().end ? "bg-[#0066CC] text-white border-[#0066CC]" : "bg-white text-slate-600 border-slate-200 hover:border-[#0066CC] hover:text-[#0066CC]"}`}
                      >
                        {chip.label}
                      </button>
                    ))}
                  </div>
                  {/* Date range + Project + Search */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                    <div>
                      <Label className="text-[11px] text-slate-500 mb-1 block">From Date</Label>
                      <Input data-testid="list-receipts-start" type="date" value={listFilters.start} onChange={e => setListFilters(p => ({ ...p, start: e.target.value }))} className="h-9 text-xs" />
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-500 mb-1 block">To Date</Label>
                      <Input data-testid="list-receipts-end" type="date" value={listFilters.end} onChange={e => setListFilters(p => ({ ...p, end: e.target.value }))} className="h-9 text-xs" />
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-500 mb-1 block">Project</Label>
                      <ProjectSearch projects={projects} value={listFilters.project_id} onChange={(id) => setListFilters(p => ({ ...p, project_id: id }))} placeholder="All projects" />
                    </div>
                    <div>
                      <Label className="text-[11px] text-slate-500 mb-1 block">Search</Label>
                      <div className="relative">
                        <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-slate-400" />
                        <Input data-testid="list-receipts-search" type="text" value={listFilters.q} onChange={e => setListFilters(p => ({ ...p, q: e.target.value }))} placeholder="Project / bank ref / notes" className="h-9 text-xs pl-7" />
                      </div>
                    </div>
                    <Button variant="outline" size="icon" onClick={loadListReceipts} className="h-9 w-9" title="Refresh" data-testid="receipts-refresh-btn">
                      <RefreshCw className={`w-3.5 h-3.5 ${listLoading ? "animate-spin" : ""}`} />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Stats + Table */}
              {(() => {
                const q = (listFilters.q || "").toLowerCase().trim();
                const filtered = q
                  ? listReceipts.filter(r =>
                      (r.project_name || "").toLowerCase().includes(q) ||
                      (r.bank_reference || "").toLowerCase().includes(q) ||
                      (r.notes || "").toLowerCase().includes(q))
                  : listReceipts;
                const totalAmt = filtered.reduce((s, r) => s + (r.collection_amount || 0), 0);
                return (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="bg-white rounded-lg border p-3 text-center">
                        <p className="text-xl font-bold text-slate-800">{filtered.length}</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Receipts</p>
                      </div>
                      <div className="bg-white rounded-lg border p-3 text-center">
                        <p className="text-xl font-bold text-emerald-600">{fmt(totalAmt)}</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Total Collected</p>
                      </div>
                      <div className="bg-white rounded-lg border p-3 text-center">
                        <p className="text-xl font-bold text-[#0066CC]">{fmt(filtered.length ? totalAmt / filtered.length : 0)}</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Avg / Receipt</p>
                      </div>
                      <div className="bg-white rounded-lg border p-3 text-center">
                        <p className="text-xl font-bold text-slate-800">{new Set(filtered.map(r => r.project_id)).size}</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">Projects</p>
                      </div>
                    </div>

                    <Card className="border-slate-200">
                      <CardContent className="p-0">
                        <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
                          <table className="w-full text-xs" data-testid="list-receipts-table">
                            <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10 shadow-[0_1px_0_rgba(0,0,0,0.06)]">
                              <tr>
                                <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Date</th>
                                <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Project</th>
                                <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Customer</th>
                                <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Bank Ref</th>
                                <th className="text-left py-2.5 px-3 font-semibold text-slate-600">Notes</th>
                                <th className="text-right py-2.5 px-3 font-semibold text-slate-600">Amount</th>
                                <th className="text-center py-2.5 px-3 font-semibold text-slate-600 w-12">·</th>
                              </tr>
                            </thead>
                            <tbody>
                              {listLoading ? (
                                <tr><td colSpan="7" className="text-center py-12 text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
                              ) : filtered.length === 0 ? (
                                <tr><td colSpan="7" className="text-center py-12 text-slate-400">No receipts match these filters.</td></tr>
                              ) : (
                                (() => {
                                  const start = (listPage - 1) * listPageSize;
                                  const pageSlice = filtered.slice(start, start + listPageSize);
                                  return pageSlice.map(r => (
                                    <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                                      <td className="py-2.5 px-3 text-slate-700 whitespace-nowrap">{new Date(r.receipt_date + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" })}</td>
                                      <td className="py-2.5 px-3 font-medium text-slate-900">{r.project_name}</td>
                                      <td className="py-2.5 px-3 text-slate-600">{r.customer_name || "—"}</td>
                                      <td className="py-2.5 px-3 text-blue-600">{r.bank_reference || "—"}</td>
                                      <td className="py-2.5 px-3 text-slate-500 max-w-[260px] truncate" title={r.notes}>{r.notes || "—"}</td>
                                      <td className="py-2.5 px-3 text-right font-bold text-emerald-600 whitespace-nowrap">{fmt(r.collection_amount)}</td>
                                      <td className="py-2.5 px-3 text-center"><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => deleteReceipt(r.id)}><Trash2 className="w-3.5 h-3.5" /></Button></td>
                                    </tr>
                                  ));
                                })()
                              )}
                            </tbody>
                            {filtered.length > 0 && (
                              <tfoot className="bg-slate-50 border-t border-slate-200 sticky bottom-0 z-10 shadow-[0_-1px_0_rgba(0,0,0,0.06)]">
                                <tr>
                                  <td colSpan="5" className="py-2.5 px-3 font-semibold text-slate-700">Total ({filtered.length} receipt{filtered.length !== 1 ? "s" : ""})</td>
                                  <td className="py-2.5 px-3 text-right font-bold text-emerald-700">{fmt(totalAmt)}</td>
                                  <td></td>
                                </tr>
                              </tfoot>
                            )}
                          </table>
                        </div>
                        {filtered.length > 0 && (
                          <PaginationBar
                            total={filtered.length}
                            page={listPage}
                            pageSize={listPageSize}
                            onPageChange={setListPage}
                            onPageSizeChange={setListPageSize}
                            testId="list-receipts-pagination"
                          />
                        )}
                      </CardContent>
                    </Card>
                  </>
                );
              })()}
            </div>
          )}

          {/* REPORTS */}
          {activeTab === "reports" && <ReportsPanel projects={projects} onSnapshot={loadSnapshot} />}

          {/* ANALYTICS */}
          {activeTab === "analytics" && (
            <CollectionAnalytics
              analytics={analytics}
              fyComparison={fyComparison}
              yoyComparison={yoyData}
              selectedFys={selectedFys}
              fyTargets={fyTargets}
              loading={loadingAnalytics}
              loadingAi={loadingAi}
              aiInsights={aiInsights}
              onRefresh={loadAnalytics}
              onGenerateAi={generateAiInsights}
              onFysChange={(fys) => { setSelectedFys(fys); loadFyComparison(fys); }}
            />
          )}

          {/* SETTINGS */}
          {activeTab === "settings" && (
            <div className="space-y-6">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Collection Targets</h1>
                <p className="text-xs text-slate-500 mt-1">Default targets are used as fallback when no FY-specific target is configured.</p>
              </div>

              {/* Default Targets */}
              <Card>
                <CardContent className="p-5 sm:p-6 space-y-5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-sm font-semibold text-slate-800">Default Targets (fallback)</h2>
                    <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded font-semibold">Used when no FY target found</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {[["daily_target", "Daily"], ["weekly_target", "Weekly"], ["monthly_target", "Monthly"], ["annual_target", "Annual"]].map(([k, l]) => (
                      <div key={k}>
                        <Label className="text-xs font-semibold text-slate-600">{l} Target</Label>
                        <div className="relative mt-1">
                          <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input type="text" inputMode="numeric" autoComplete="off" className="pl-9 h-11 text-base font-medium" value={targets[k] ? String(targets[k]) : ""} onFocus={(e) => e.target.select()} onChange={e => { const clean = sanitizeNumeric(e.target.value); setTargets(p => ({ ...p, [k]: clean === "" ? 0 : parseInt(clean, 10) })); }} placeholder="0" data-testid={`default-${k}`} />
                          <p className="text-[10px] text-slate-400 mt-1">≈ ₹{((targets[k] || 0) / 100000).toFixed(2)} L</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Button onClick={handleSaveTargets} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3] w-full sm:w-auto sm:px-8 h-11" data-testid="save-default-targets">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Default Targets</Button>
                </CardContent>
              </Card>

              {/* FY Targets */}
              <Card>
                <CardContent className="p-5 sm:p-6 space-y-5">
                  <div>
                    <h2 className="text-sm font-semibold text-slate-800">Financial Year Targets</h2>
                    <p className="text-[11px] text-slate-500 mt-0.5">Indian FY (Apr–Mar). Used by Reports & Analytics for the matching period.</p>
                  </div>

                  {/* Add / Edit form */}
                  <div className="bg-slate-50 rounded-xl p-5 space-y-4 border border-slate-200">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="text-sm font-semibold text-slate-700">{editingFy ? `Editing: ${editingFy.fy_label}` : "Add Financial Year Target"}</p>
                      {!editingFy && (
                        <p className="text-[11px] text-slate-500">FY <strong>{fyForm.fy_start_year}-{String(fyForm.fy_start_year + 1).slice(-2)}</strong></p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                      <div>
                        <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">FY Start Year</Label>
                        <Input type="text" inputMode="numeric" autoComplete="off" maxLength="4" className="h-11 text-base font-medium mt-1" value={fyForm.fy_start_year ? String(fyForm.fy_start_year) : ""} onFocus={(e) => e.target.select()} onChange={e => { const clean = sanitizeNumeric(e.target.value).slice(0, 4); setFyForm(p => ({ ...p, fy_start_year: clean === "" ? 0 : parseInt(clean, 10) })); }} disabled={!!editingFy} placeholder="2025" data-testid="fy-year-input" />
                      </div>
                      {[["daily_target", "Daily"], ["weekly_target", "Weekly"], ["monthly_target", "Monthly"], ["annual_target", "Annual"]].map(([k, l]) => (
                        <div key={k}>
                          <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">{l}</Label>
                          <div className="relative mt-1">
                            <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                            <Input type="text" inputMode="numeric" autoComplete="off" className="pl-8 h-11 text-base font-medium" value={fyForm[k] ? String(fyForm[k]) : ""} onFocus={(e) => e.target.select()} onChange={e => { const clean = sanitizeNumeric(e.target.value); setFyForm(p => ({ ...p, [k]: clean === "" ? 0 : parseInt(clean, 10) })); }} placeholder="0" data-testid={`fy-${k}`} />
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1">≈ ₹{((fyForm[k] || 0) / 100000).toFixed(2)} L</p>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2 justify-end pt-1">
                      {editingFy && <Button variant="ghost" size="sm" className="h-10" onClick={() => { setEditingFy(null); setFyForm({ fy_start_year: new Date().getMonth() >= 3 ? new Date().getFullYear() : new Date().getFullYear() - 1, daily_target: 0, weekly_target: 0, monthly_target: 0, annual_target: 0 }); }}>Cancel</Button>}
                      <Button onClick={handleSaveFy} disabled={saving || !fyForm.fy_start_year} className="bg-[#0066CC] hover:bg-[#0052A3] gap-2 h-10 px-5" data-testid="save-fy-target">
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} {editingFy ? "Update FY Target" : "Add FY Target"}
                      </Button>
                    </div>
                  </div>

                  {/* List of FY Targets */}
                  <div>
                    {fyTargets.length === 0 ? (
                      <p className="text-center text-xs text-slate-400 py-6">No FY-specific targets yet. Defaults above will be used.</p>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-slate-200">
                        <table className="w-full text-xs sm:text-sm" data-testid="fy-targets-table">
                          <thead className="bg-slate-100">
                            <tr>
                              <th className="text-left p-3 font-semibold text-slate-600">FY</th>
                              <th className="text-right p-3 font-semibold text-slate-600 hidden sm:table-cell">Daily</th>
                              <th className="text-right p-3 font-semibold text-slate-600 hidden sm:table-cell">Weekly</th>
                              <th className="text-right p-3 font-semibold text-slate-600">Monthly</th>
                              <th className="text-right p-3 font-semibold text-slate-600">Annual</th>
                              <th className="text-right p-3 font-semibold text-slate-600 w-[100px]">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fyTargets.map(fy => (
                              <tr key={fy.id} className="border-t border-slate-100 hover:bg-slate-50">
                                <td className="p-3 font-semibold text-slate-800">{fy.fy_label}</td>
                                <td className="p-3 text-right text-slate-700 hidden sm:table-cell">{fmt(fy.daily_target)}</td>
                                <td className="p-3 text-right text-slate-700 hidden sm:table-cell">{fmt(fy.weekly_target)}</td>
                                <td className="p-3 text-right text-slate-700">{fmt(fy.monthly_target)}</td>
                                <td className="p-3 text-right font-semibold text-emerald-600">{fmt(fy.annual_target)}</td>
                                <td className="p-3 text-right whitespace-nowrap">
                                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => handleEditFy(fy)}><Edit2 className="w-3.5 h-3.5" /></Button>
                                  <Button variant="ghost" size="sm" className="h-7 text-xs text-red-600" onClick={() => handleDeleteFy(fy.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Data Eraser */}
              <Card className="border-red-200">
                <CardContent className="p-5 sm:p-6 space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-red-50 flex items-center justify-center shrink-0">
                      <Trash2 className="w-4 h-4 text-red-600" />
                    </div>
                    <div>
                      <h2 className="text-sm font-semibold text-slate-800">Data Eraser — Receipts</h2>
                      <p className="text-[11px] text-slate-500 mt-0.5">Permanently delete all receipt records within a selected date range. This action cannot be undone.</p>
                    </div>
                  </div>

                  {/* Quick range chips — removes MM/DD vs DD/MM ambiguity */}
                  <div className="flex flex-wrap gap-1.5">
                    <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider self-center mr-1">Quick select:</span>
                    {[
                      { key: "currentMonth", label: "Current Month" },
                      { key: "currentFY", label: "Current FY" },
                      { key: "last90", label: "Last 90 Days" },
                      { key: "allTime", label: "All Time" },
                    ].map(c => (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => applyEraserQuickRange(c.key)}
                        className="text-[11px] px-2.5 py-1 rounded-full border border-slate-200 bg-white hover:bg-slate-50 hover:border-[#0066CC] hover:text-[#0066CC] transition-colors"
                        data-testid={`eraser-quick-${c.key}`}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                    <div>
                      <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Start Date</Label>
                      <Input type="date" className="h-11 mt-1" value={eraseRange.start} onChange={e => { setEraseRange(p => ({ ...p, start: e.target.value })); setErasePreview(null); }} data-testid="eraser-start-date" />
                      <p className="text-[10px] text-slate-500 mt-1">{eraseRange.start ? fmtDateLong(eraseRange.start) : "— pick a date —"}</p>
                    </div>
                    <div>
                      <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">End Date</Label>
                      <Input type="date" className="h-11 mt-1" value={eraseRange.end} onChange={e => { setEraseRange(p => ({ ...p, end: e.target.value })); setErasePreview(null); }} data-testid="eraser-end-date" />
                      <p className="text-[10px] text-slate-500 mt-1">{eraseRange.end ? fmtDateLong(eraseRange.end) : "— pick a date —"}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" className="h-11 flex-1" onClick={handleErasePreview} disabled={erasePreviewLoading || !eraseRange.start || !eraseRange.end} data-testid="eraser-preview-btn">
                        {erasePreviewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />} Preview
                      </Button>
                    </div>
                  </div>

                  {erasePreview ? (
                    <div
                      ref={erasePreviewRef}
                      className={`rounded-lg border p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${erasePreview.count > 0 ? "border-red-200 bg-red-50/60" : "border-slate-200 bg-slate-50"}`}
                      data-testid="eraser-preview-result"
                    >
                      <div className="text-sm text-slate-800">
                        {erasePreview.count > 0 ? (
                          <>
                            <p>
                              <span className="font-bold text-red-700">{erasePreview.count}</span> receipt{erasePreview.count === 1 ? "" : "s"} totalling <span className="font-bold text-red-700">₹{fmt(erasePreview.total_amount)}</span> between
                              <span className="font-semibold"> {fmtDateLong(erasePreview.start_date)}</span> and
                              <span className="font-semibold"> {fmtDateLong(erasePreview.end_date)}</span>.
                            </p>
                            <p className="text-[11px] text-red-600 mt-1">This will permanently remove these records from the database.</p>
                          </>
                        ) : (
                          <div>
                            <p className="text-slate-600">
                              <span className="font-bold text-slate-800">No receipts</span> found between <span className="font-semibold">{fmtDateLong(erasePreview.start_date)}</span> and <span className="font-semibold">{fmtDateLong(erasePreview.end_date)}</span>.
                            </p>
                            <p className="text-[11px] text-slate-500 mt-1">Tip: double-check the dates above — date inputs may show in your browser's regional format. The unambiguous dates are shown below each input.</p>
                          </div>
                        )}
                      </div>
                      <Button
                        className="bg-red-600 hover:bg-red-700 text-white h-10 px-5 shrink-0 disabled:bg-slate-300"
                        disabled={erasePreview.count === 0}
                        onClick={() => setEraseConfirmOpen(true)}
                        data-testid="eraser-erase-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2" /> Erase {erasePreview.count > 0 ? `${erasePreview.count} Receipt${erasePreview.count === 1 ? "" : "s"}` : "Data"}
                      </Button>
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 italic">Pick a quick range above or select dates manually, then click <span className="font-medium">Preview</span> — the count of matching receipts will appear here.</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </main>

      {/* Project Dialog */}
      <Dialog open={showProjectDialog} onOpenChange={setShowProjectDialog}>
        <DialogContent className="max-w-[420px]"><DialogHeader><DialogTitle>{editingProject ? "Edit Project" : "New Project"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Project Name *</Label><Input value={projectForm.name} onChange={e => setProjectForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. ABC Corp ERP" /></div>
            <div><Label className="text-xs">Type</Label>
              <select className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" value={projectForm.project_type} onChange={e => setProjectForm(p => ({ ...p, project_type: e.target.value }))} data-testid="project-type-select">
                {REVENUE_STREAMS.filter(s => !s.legacy).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                {/* Show legacy value if currently editing a project that has one */}
                {editingProject && REVENUE_STREAMS.find(s => s.key === projectForm.project_type)?.legacy && (
                  <option value={projectForm.project_type}>{REVENUE_STREAMS.find(s => s.key === projectForm.project_type)?.label}</option>
                )}
              </select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Order Date</Label><Input type="date" value={projectForm.order_date} onChange={e => setProjectForm(p => ({ ...p, order_date: e.target.value }))} /></div>
              <div><Label className="text-xs">Start Date</Label><Input type="date" value={projectForm.start_date} onChange={e => setProjectForm(p => ({ ...p, start_date: e.target.value }))} /></div>
            </div>
            <div><Label className="text-xs">Project Amount (INR) *</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type="text" inputMode="numeric" autoComplete="off" className="pl-9 h-11 text-base font-medium" value={projectForm.project_amount ? String(projectForm.project_amount) : ""} onFocus={(e) => e.target.select()} onChange={e => { const c = sanitizeNumeric(e.target.value); setProjectForm(p => ({ ...p, project_amount: c === "" ? 0 : parseInt(c, 10) })); }} placeholder="0" data-testid="project-amount-input" />
              </div>
              <p className="text-[10px] text-slate-400 mt-1">≈ ₹{((projectForm.project_amount || 0) / 100000).toFixed(2)} L</p>
            </div>
            <div><Label className="text-xs">Notes</Label><Textarea value={projectForm.notes} onChange={e => setProjectForm(p => ({ ...p, notes: e.target.value }))} rows={2} /></div>
            <div>
              <Label className="text-xs">Purchase Order URL <span className="text-slate-400 font-normal">(optional)</span></Label>
              <div className="flex gap-2">
                <Input
                  type="url"
                  value={projectForm.purchase_order_url}
                  onChange={e => setProjectForm(p => ({ ...p, purchase_order_url: e.target.value.trim() }))}
                  placeholder="https://onedrive.live.com/... or ftp://server/po.pdf"
                  data-testid="project-po-url-input"
                />
                {projectForm.purchase_order_url && /^https?:\/\//.test(projectForm.purchase_order_url) && (
                  <Button type="button" variant="outline" size="icon" onClick={() => window.open(projectForm.purchase_order_url, "_blank", "noopener")} title="Open PO" data-testid="project-po-preview-btn">
                    <Eye className="w-4 h-4" />
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Paste any link — OneDrive, Google Drive, FTP, or a direct PDF/image URL. Use the eye icon to preview before saving.</p>
            </div>
            <Button onClick={handleSaveProject} disabled={saving} className="w-full bg-[#0066CC] hover:bg-[#0052A3]">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} {editingProject ? "Update" : "Create"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Entry Dialog (Weekly) */}
      <Dialog open={showEntryDialog} onOpenChange={setShowEntryDialog}>
        <DialogContent className="max-w-[400px]"><DialogHeader><DialogTitle>{entryForm.id ? "Edit Collection Plan Entry" : "Add Collection Plan Entry"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Project *</Label><ProjectSearch projects={projects.filter(p => p.status === "active")} value={entryForm.project_id} onChange={id => setEntryForm(p => ({ ...p, project_id: id }))} /></div>
            {entryForm.project_id && (() => { const proj = projects.find(p => p.id === entryForm.project_id); return proj ? <p className="text-xs text-slate-500 bg-slate-50 p-2 rounded">Pending due: <strong className="text-red-600">{fmt(proj.outstanding || 0)}</strong></p> : null; })()}
            <div><Label className="text-xs">Date *</Label><Input type="date" value={entryForm.date} onChange={e => setEntryForm(p => ({ ...p, date: e.target.value }))} /></div>
            <div><Label className="text-xs">Planned Amount (INR) *</Label>
              <div className="relative">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  className="pl-9 h-11 text-base font-medium"
                  value={entryForm.planned_amount ? String(entryForm.planned_amount) : ""}
                  onFocus={(e) => e.target.select()}
                  onChange={e => { const c = sanitizeNumeric(e.target.value); setEntryForm(p => ({ ...p, planned_amount: c === "" ? 0 : parseInt(c, 10) })); }}
                  placeholder="0"
                  data-testid="entry-planned-amount-input"
                />
              </div>
              <p className="text-[10px] text-slate-400 mt-1">≈ ₹{((entryForm.planned_amount || 0) / 100000).toFixed(2)} L</p>
            </div>
            <div><Label className="text-xs">Notes</Label><Input value={entryForm.notes} onChange={e => setEntryForm(p => ({ ...p, notes: e.target.value }))} /></div>
            <Button onClick={handleSaveEntry} disabled={saving} className="w-full bg-[#0066CC] hover:bg-[#0052A3]" data-testid="save-entry-btn">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : (entryForm.id ? <Save className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />)} {entryForm.id ? "Save Changes" : "Add Entry"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Dialog */}
      <Dialog open={showReceiptDialog} onOpenChange={setShowReceiptDialog}>
        <DialogContent className="max-w-[420px]"><DialogHeader><DialogTitle>New Collection Receipt</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label className="text-xs font-medium">Project *</Label><ProjectSearch projects={projects.filter(p => p.status === "active")} value={receiptForm.project_id} onChange={id => { api.get("/collections/customers").then(r => { const custs = r.data || []; setReceiptCustomers(custs); const tagged = custs.find(c => c.project_id === id); setReceiptForm(p => ({ ...p, project_id: id, customer_id: tagged ? tagged.id : "" })); }).catch(() => { setReceiptForm(p => ({ ...p, project_id: id })); }); }} /></div>
            <div>
              <Label className="text-xs font-medium">Customer</Label>
              <select value={receiptForm.customer_id} onClick={() => { api.get("/collections/customers").then(r => setReceiptCustomers(r.data || [])).catch(() => {}); }} onChange={e => setReceiptForm(p => ({ ...p, customer_id: e.target.value }))} className="w-full h-9 mt-1 text-sm border border-slate-200 rounded px-2 bg-white" data-testid="receipt-customer-select">
                <option value="">— Select Customer —</option>
                {receiptCustomers.filter(c => !receiptForm.project_id || c.project_id === receiptForm.project_id).map(c => <option key={c.id} value={c.id}>{c.name} ({c.business_type})</option>)}
              </select>
              <p className="text-[10px] text-slate-400 mt-1 italic">Pick a project first to see customers tagged to it.</p>
            </div>
            <div><Label className="text-xs font-medium">Receipt Date *</Label><Input type="date" value={receiptForm.receipt_date} onChange={e => setReceiptForm(p => ({ ...p, receipt_date: e.target.value }))} className="mt-1" /></div>
            <div><Label className="text-xs font-medium">Collection Amount (INR) *</Label>
              <div className="relative mt-1">
                <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input type="text" inputMode="numeric" autoComplete="off" className="pl-9 h-11 text-base font-medium" value={receiptForm.collection_amount ? String(receiptForm.collection_amount) : ""} onFocus={(e) => e.target.select()} onChange={e => { const c = sanitizeNumeric(e.target.value); setReceiptForm(p => ({ ...p, collection_amount: c === "" ? 0 : parseInt(c, 10) })); }} placeholder="0" data-testid="receipt-amount-input" />
              </div>
              <p className="text-[10px] text-slate-400 mt-1">≈ ₹{((receiptForm.collection_amount || 0) / 100000).toFixed(2)} L</p>
            </div>
            <div><Label className="text-xs font-medium">Bank Reference</Label><Input value={receiptForm.bank_reference} onChange={e => setReceiptForm(p => ({ ...p, bank_reference: e.target.value }))} placeholder="e.g. NEFT/RTGS ref" className="mt-1" /></div>
            <Button onClick={handleSaveReceipt} disabled={saving} className="w-full bg-[#0066CC] hover:bg-[#0052A3] h-11 text-sm">{saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />} Record Receipt</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bank Statement Mapping Dialog */}
      <Dialog open={showBankDialog} onOpenChange={setShowBankDialog}>
        <DialogContent className="max-w-[900px] max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-[#0066CC]" /> Bank Statement Import</DialogTitle>
            <DialogDescription>{bankLines.length} record(s) loaded from your bank statement. Select a project for each line, then click "Map & Create Receipts".</DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between py-2">
            <p className="text-xs text-slate-500">{Object.values(bankMappings).filter(v => v).length} of {bankLines.length} mapped</p>
          </div>
          <div className="overflow-auto flex-1 border border-slate-200 rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  <th className="text-left p-2 font-semibold text-slate-600 w-[100px]">Date</th>
                  <th className="text-right p-2 font-semibold text-slate-600 w-[110px]">Amount (₹)</th>
                  <th className="text-left p-2 font-semibold text-slate-600 min-w-[180px]">Reference</th>
                  <th className="text-left p-2 font-semibold text-slate-600 min-w-[250px]">Map to Project</th>
                </tr>
              </thead>
              <tbody>
                {bankLines.map(l => (
                  <tr key={l.id} className={`border-t border-slate-100 ${bankMappings[l.id] ? "bg-emerald-50/30" : "hover:bg-slate-50"}`}>
                    <td className="p-2 font-medium text-slate-700">{l.date}</td>
                    <td className="p-2 text-right font-bold text-emerald-700">{fmt(l.amount)}</td>
                    <td className="p-2 text-slate-500 truncate max-w-[200px]" title={l.reference}>{l.reference || "—"}</td>
                    <td className="p-2">
                      <select className="w-full h-8 text-xs border border-slate-200 rounded px-2 bg-white" value={bankMappings[l.id] || ""}
                        onChange={e => setBankMappings(p => ({ ...p, [l.id]: e.target.value }))}>
                        <option value="">-- Select Project --</option>
                        {projects.filter(p => p.status === "active" || !p.status).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between pt-3 border-t">
            <p className="text-[10px] text-slate-400 italic">Only mapped lines will create receipts. Unmapped lines will be skipped.</p>
            <Button onClick={handleBankMap} disabled={saving || Object.values(bankMappings).filter(v => v).length === 0} className="bg-[#0066CC] hover:bg-[#0052A3] gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Map & Create Receipts ({Object.values(bankMappings).filter(v => v).length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Historical Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={(v) => { if (!importing) setShowImportDialog(v); }}>
        <DialogContent className="max-w-[400px]"><DialogHeader><DialogTitle>Import Historical Receipts</DialogTitle></DialogHeader>
          {importing ? (
            <div className="flex flex-col items-center justify-center gap-3 py-10">
              <Loader2 className="w-8 h-8 animate-spin text-[#0066CC]" />
              <span className="text-sm font-medium text-[#0066CC]">Importing receipts... please wait</span>
            </div>
          ) : (
          <div className="space-y-3 mt-2">
            <p className="text-xs text-slate-500">Upload an Excel file with columns: <strong>Date, Project Name, Amount, Bank Reference</strong></p>
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">Project names must match exactly with existing projects in the system.</p>
            <a href={`${api.defaults.baseURL}/collections/templates/receipts`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full gap-2 mb-2"><Download className="w-4 h-4" /> Download Receipt Template</Button>
            </a>
            <label className="block"><Button variant="outline" className="w-full gap-2 cursor-pointer" asChild><span><Upload className="w-4 h-4" /> Select Excel File to Import</span></Button><input type="file" accept=".xlsx" className="hidden" onChange={handleHistoricalImport} /></label>
          </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Import Result Dialog */}
      <Dialog open={!!importResult} onOpenChange={(v) => { if (!v) setImportResult(null); }}>
        <DialogContent className="max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {importResult?.created > 0 ? <CheckCircle className="w-5 h-5 text-emerald-500" /> : <AlertTriangle className="w-5 h-5 text-amber-500" />}
              Import {importResult?.created > 0 ? "Completed" : "Result"}
            </DialogTitle>
          </DialogHeader>
          {importResult && (
            <div className="space-y-4 mt-2">
              {/* Summary stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-emerald-700">{importResult.created || 0}</p>
                  <p className="text-[10px] text-emerald-600 font-medium">Imported</p>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-amber-700">{importResult.skipped || 0}</p>
                  <p className="text-[10px] text-amber-600 font-medium">Skipped</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-red-700">{importResult.errors?.length || 0}</p>
                  <p className="text-[10px] text-red-600 font-medium">Errors</p>
                </div>
              </div>

              {/* Error details */}
              {importResult.errors?.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                  <p className="text-xs font-semibold text-red-700 mb-1.5">Error Details:</p>
                  <ul className="space-y-1">
                    {importResult.errors.slice(0, 10).map((err, i) => (
                      <li key={i} className="text-[11px] text-red-600">• {err}</li>
                    ))}
                    {importResult.errors.length > 10 && (
                      <li className="text-[11px] text-red-500 italic">...and {importResult.errors.length - 10} more</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Status message */}
              {importResult.created === 0 && importResult.skipped > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">All records already exist — no duplicates were imported.</p>
              )}
              {importResult.created === 0 && (importResult.skipped || 0) === 0 && (importResult.errors?.length || 0) === 0 && (
                <p className="text-xs text-slate-600 bg-slate-50 p-2 rounded">The file appears to be empty — no data rows found. Please check the file and try again.</p>
              )}

              <Button onClick={() => setImportResult(null)} className="w-full bg-[#0066CC] hover:bg-[#0052A3]">Close</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Project Snapshot Dialog */}
      <Dialog open={showSnapshot} onOpenChange={setShowSnapshot}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
          {snapshotData && (
            <div id="snapshot-card" className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6">
              <div className="flex items-center justify-between mb-4">
                <img src={LOGO_URL} alt="Absolin" className="h-6 brightness-200" />
                <span className="text-[10px] text-slate-400">Project Snapshot</span>
              </div>
              <h2 className="text-lg font-bold mb-1">{snapshotData.name}</h2>
              <span className="text-xs text-slate-400 capitalize">{snapshotData.project_type?.replace(/_/g, " ")} • {snapshotData.status}</span>
              <div className="grid grid-cols-3 gap-3 mt-5">
                <div className="bg-white/10 rounded-lg p-3 text-center"><p className="text-base font-bold text-[#4d9dff] tabular-nums">{fmtFull(snapshotData.project_amount)}</p><p className="text-[10px] text-slate-400">Project Value</p></div>
                <div className="bg-white/10 rounded-lg p-3 text-center"><p className="text-base font-bold text-emerald-400 tabular-nums">{fmtFull(snapshotData.total_collected)}</p><p className="text-[10px] text-slate-400">Collected</p></div>
                <div className="bg-white/10 rounded-lg p-3 text-center"><p className="text-base font-bold text-red-400 tabular-nums">{fmtFull(snapshotData.outstanding)}</p><p className="text-[10px] text-slate-400">Due</p></div>
              </div>
              <div className="mt-4 h-2 bg-white/10 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${snapshotData.collection_pct}%` }} /></div>
              <p className="text-xs text-slate-400 mt-1 text-right">{snapshotData.collection_pct}% collected • {snapshotData.receipt_count} receipts</p>
              <p className="text-[8px] text-slate-500 mt-4 text-center">Generated by Absolin Collection Planning</p>
            </div>
          )}

          {/* All Receipts (outside the snapshot image) */}
          {snapshotData?.all_receipts?.length > 0 && (
            <div className="p-4 sm:p-6 bg-white border-t border-slate-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-slate-800">All Receipts ({snapshotData.all_receipts.length})</h3>
                <span className="text-[11px] text-slate-500">Total: <strong className="text-emerald-600">{fmtFull(snapshotData.total_collected)}</strong></span>
              </div>
              <div className="overflow-x-auto max-h-72 overflow-y-auto rounded-lg border border-slate-200">
                <table className="w-full text-xs" data-testid="snapshot-receipts-table">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2.5 font-semibold text-slate-600">Receipt Date</th>
                      <th className="text-right p-2.5 font-semibold text-slate-600">Amount</th>
                      <th className="text-left p-2.5 font-semibold text-slate-600 hidden sm:table-cell">Bank Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {snapshotData.all_receipts.map(r => (
                      <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="p-2.5 text-slate-700 whitespace-nowrap">{r.receipt_date}</td>
                        <td className="p-2.5 text-right font-semibold text-emerald-600 tabular-nums">{fmtFull(r.collection_amount)}</td>
                        <td className="p-2.5 text-slate-500 text-[11px] hidden sm:table-cell">{r.bank_reference || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="p-3 bg-white flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={() => setShowSnapshot(false)}>Close</Button>
            <Button size="sm" className="bg-[#0066CC] gap-1.5" onClick={() => {
              const el = document.getElementById("snapshot-card");
              if (el) { import("html2canvas").then(mod => mod.default(el, { scale: 2 }).then(c => { const link = document.createElement("a"); link.download = `${snapshotData?.name || "project"}_snapshot.png`; link.href = c.toDataURL(); link.click(); })); }
            }}><Camera className="w-3.5 h-3.5" /> Save as Image</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Projects Dialog */}
      <ProjectsBulkEditDialog open={showBulkEdit} onOpenChange={setShowBulkEdit} projects={projects} onSaved={loadProjects} />

      {/* Data Eraser Confirmation Dialog */}
      <Dialog open={eraseConfirmOpen} onOpenChange={(o) => !erasing && setEraseConfirmOpen(o)}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Confirm Permanent Erase
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2 text-sm text-slate-700">
            <p>
              You are about to permanently delete <span className="font-bold text-red-700">{erasePreview?.count || 0}</span> receipt{(erasePreview?.count || 0) === 1 ? "" : "s"} totalling
              <span className="font-bold text-red-700"> ₹{fmt(erasePreview?.total_amount || 0)}</span> recorded between <span className="font-semibold">{fmtDateLong(erasePreview?.start_date)}</span> and <span className="font-semibold">{fmtDateLong(erasePreview?.end_date)}</span>.
            </p>
            <p className="text-xs text-red-600">This action cannot be undone.</p>
          </div>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="ghost" disabled={erasing} onClick={() => setEraseConfirmOpen(false)} data-testid="eraser-cancel-btn">Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={erasing} onClick={handleEraseConfirm} data-testid="eraser-confirm-btn">
              {erasing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />} Yes, Erase Permanently
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== Date Range Presets (India FY = Apr 1 — Mar 31) =====
function _fmtDate(d) { return d.toISOString().split("T")[0]; }
function _addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
// ===== Projects Bulk Edit Dialog =====
function ProjectsBulkEditDialog({ open, onOpenChange, projects, onSaved }) {
  const [rows, setRows] = useState([]);
  const [savingId, setSavingId] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open) return;
    setRows(projects.map(p => ({
      id: p.id,
      name: p.name || "",
      project_type: p.project_type || "",
      project_amount: p.project_amount || 0,
      status: p.status || "active",
      start_date: p.start_date || "",
      _dirty: false,
    })));
  }, [open, projects]);

  const update = (id, patch) => setRows(rs => rs.map(r => r.id === id ? { ...r, ...patch, _dirty: true } : r));

  const saveRow = async (row) => {
    setSavingId(row.id);
    try {
      await api.put(`/collections/projects/${row.id}`, {
        name: row.name,
        project_type: row.project_type,
        project_amount: Number(row.project_amount) || 0,
        status: row.status,
        start_date: row.start_date,
      });
      setRows(rs => rs.map(r => r.id === row.id ? { ...r, _dirty: false } : r));
      toast.success(`Saved: ${row.name}`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
    setSavingId(null);
  };

  const filtered = rows.filter(r => !search || r.name.toLowerCase().includes(search.toLowerCase()));
  const dirtyCount = rows.filter(r => r._dirty).length;

  const saveAllDirty = async () => {
    for (const r of rows.filter(x => x._dirty)) await saveRow(r);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1200px] max-h-[90vh] overflow-hidden flex flex-col" data-testid="bulk-edit-projects-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ListChecks className="w-5 h-5 text-[#0066CC]" /> Bulk Edit Projects</DialogTitle>
          <DialogDescription>Inline-edit project name, type, amount, status and start date. Save each row individually or use Save All.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-between gap-2 py-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
            <Input className="pl-8 h-9" placeholder="Filter projects..." value={search} onChange={e => setSearch(e.target.value)} data-testid="bulk-edit-search" />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{dirtyCount} unsaved</span>
            <Button onClick={saveAllDirty} disabled={!dirtyCount || !!savingId} className="bg-[#0066CC] hover:bg-[#0052A3] gap-1.5 h-9" data-testid="bulk-edit-save-all">
              <Save className="w-3.5 h-3.5" /> Save All ({dirtyCount})
            </Button>
          </div>
        </div>
        <div className="overflow-auto flex-1 border border-slate-200 rounded-lg">
          <table className="w-full text-xs" data-testid="bulk-edit-table">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[260px]">Project Name</th>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[180px]">Type</th>
                <th className="text-right p-2 font-semibold text-slate-600 min-w-[140px]">Amount (₹)</th>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[110px]">Status</th>
                <th className="text-left p-2 font-semibold text-slate-600 min-w-[140px]">Start Date</th>
                <th className="text-center p-2 font-semibold text-slate-600 sticky right-0 bg-slate-50 w-[80px]">Save</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(r => (
                <tr key={r.id} className={`border-t border-slate-100 ${r._dirty ? "bg-amber-50/40" : "hover:bg-slate-50"}`}>
                  <td className="p-1.5"><Input className="h-8 text-xs" value={r.name} onChange={e => update(r.id, { name: e.target.value })} data-testid={`bulk-row-name-${r.id}`} /></td>
                  <td className="p-1.5">
                    <select value={r.project_type} onChange={e => update(r.id, { project_type: e.target.value })} className="w-full h-8 text-xs border border-slate-200 rounded px-2" data-testid={`bulk-row-type-${r.id}`}>
                      {REVENUE_STREAMS.filter(s => !s.legacy).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                      {REVENUE_STREAMS.find(s => s.key === r.project_type)?.legacy && <option value={r.project_type}>{REVENUE_STREAMS.find(s => s.key === r.project_type).label}</option>}
                    </select>
                  </td>
                  <td className="p-1.5"><Input type="number" className="h-8 text-xs text-right tabular-nums" value={r.project_amount} onChange={e => update(r.id, { project_amount: e.target.value })} data-testid={`bulk-row-amount-${r.id}`} /></td>
                  <td className="p-1.5">
                    <select value={r.status} onChange={e => update(r.id, { status: e.target.value })} className="w-full h-8 text-xs border border-slate-200 rounded px-2" data-testid={`bulk-row-status-${r.id}`}>
                      <option value="active">Active</option>
                      <option value="completed">Completed</option>
                      <option value="on_hold">On Hold</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td className="p-1.5"><Input type="date" className="h-8 text-xs" value={r.start_date} onChange={e => update(r.id, { start_date: e.target.value })} data-testid={`bulk-row-date-${r.id}`} /></td>
                  <td className="p-1.5 sticky right-0 bg-white text-center">
                    <Button size="sm" variant="ghost" disabled={!r._dirty || savingId === r.id} onClick={() => saveRow(r)} className="h-8 w-full text-xs" data-testid={`bulk-row-save-${r.id}`}>
                      {savingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (r._dirty ? <Save className="w-3.5 h-3.5 text-[#0066CC]" /> : <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />)}
                    </Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-slate-400 text-xs">No projects match filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-slate-400 italic">Tip: rows turn yellow when modified. Click the row's Save icon, or use Save All to commit every change at once.</p>
      </DialogContent>
    </Dialog>
  );
}

function getDatePreset(preset) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-indexed
  if (preset === "current_fy") {
    const fyStart = m >= 3 ? new Date(y, 3, 1) : new Date(y - 1, 3, 1);
    return { start: _fmtDate(fyStart), end: _fmtDate(now), label: m >= 3 ? `FY${String(y).slice(-2)}-${String(y + 1).slice(-2)}` : `FY${String(y - 1).slice(-2)}-${String(y).slice(-2)}` };
  }
  if (preset === "last_fy") {
    const fyEnd = m >= 3 ? new Date(y, 2, 31) : new Date(y - 1, 2, 31);
    const fyStart = m >= 3 ? new Date(y - 1, 3, 1) : new Date(y - 2, 3, 1);
    return { start: _fmtDate(fyStart), end: _fmtDate(fyEnd), label: `FY${String(fyStart.getFullYear()).slice(-2)}-${String(fyEnd.getFullYear()).slice(-2)}` };
  }
  if (preset === "current_month") return { start: _fmtDate(new Date(y, m, 1)), end: _fmtDate(now), label: now.toLocaleDateString("en-IN", { month: "long", year: "numeric" }) };
  if (preset === "previous_month") {
    const start = new Date(y, m - 1, 1);
    const end = new Date(y, m, 0);
    return { start: _fmtDate(start), end: _fmtDate(end), label: start.toLocaleDateString("en-IN", { month: "long", year: "numeric" }) };
  }
  if (preset === "current_quarter") {
    let qStartMonth;
    if (m >= 3 && m <= 5) qStartMonth = 3;
    else if (m >= 6 && m <= 8) qStartMonth = 6;
    else if (m >= 9 && m <= 11) qStartMonth = 9;
    else qStartMonth = 0;
    const start = new Date(qStartMonth === 0 ? y : y, qStartMonth, 1);
    if (qStartMonth === 0 && m < 3) start.setFullYear(y);
    return { start: _fmtDate(start), end: _fmtDate(now), label: ["Q4", "Q4", "Q4", "Q1", "Q1", "Q1", "Q2", "Q2", "Q2", "Q3", "Q3", "Q3"][m] };
  }
  if (preset === "previous_quarter") {
    let qStart, qEnd;
    if (m >= 3 && m <= 5) { qStart = new Date(y - 1, 0, 1); qEnd = new Date(y - 1, 2, 31); }   // prev Q4 (Jan-Mar prev year)
    else if (m >= 6 && m <= 8) { qStart = new Date(y, 3, 1); qEnd = new Date(y, 5, 30); }       // prev Q1 (Apr-Jun)
    else if (m >= 9 && m <= 11) { qStart = new Date(y, 6, 1); qEnd = new Date(y, 8, 30); }      // prev Q2 (Jul-Sep)
    else { qStart = new Date(y - 1, 9, 1); qEnd = new Date(y - 1, 11, 31); }                    // prev Q3 (Oct-Dec prev year)
    return { start: _fmtDate(qStart), end: _fmtDate(qEnd), label: ["Q3", "Q3", "Q3", "Q4", "Q4", "Q4", "Q1", "Q1", "Q1", "Q2", "Q2", "Q2"][m] };
  }
  if (preset === "as_on_date") return { start: _fmtDate(now), end: _fmtDate(now), label: "Today" };
  return { start: _fmtDate(now), end: _fmtDate(now), label: "Today" };
}
const PRESETS = [
  { id: "current_fy",       label: "Current FY" },
  { id: "current_month",    label: "Current Month" },
  { id: "previous_month",   label: "Previous Month" },
  { id: "current_quarter",  label: "Current Quarter" },
  { id: "previous_quarter", label: "Previous Quarter" },
  { id: "last_fy",          label: "Last FY" },
  { id: "as_on_date",       label: "As on Date" },
];

// ===== Pagination footer =====
function PaginationBar({ total, page, pageSize, onPageChange, onPageSizeChange, testId = "pagination" }) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  const go = (n) => onPageChange(Math.max(1, Math.min(pageCount, n)));
  return (
    <div className="flex items-center justify-between flex-wrap gap-2 px-4 py-2.5 border-t border-slate-100 bg-slate-50/60 text-[11px] text-slate-600" data-testid={testId}>
      <div className="flex items-center gap-2">
        <span>Rows per page</span>
        <select value={pageSize} onChange={(e) => { onPageSizeChange(Number(e.target.value)); onPageChange(1); }} className="h-7 border border-slate-200 rounded px-1.5 text-[11px] bg-white">
          {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
        </select>
        <span className="text-slate-400">·</span>
        <span>Showing <strong className="text-slate-800">{from}-{to}</strong> of <strong className="text-slate-800">{total}</strong></span>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => go(1)} disabled={page === 1} data-testid={`${testId}-first`}><ChevronsLeft className="w-3.5 h-3.5" /></Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => go(page - 1)} disabled={page === 1} data-testid={`${testId}-prev`}><ChevronLeft className="w-3.5 h-3.5" /></Button>
        <span className="px-2 font-medium text-slate-700 min-w-[60px] text-center">{page} / {pageCount}</span>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => go(page + 1)} disabled={page >= pageCount} data-testid={`${testId}-next`}><ChevronRight className="w-3.5 h-3.5" /></Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => go(pageCount)} disabled={page >= pageCount} data-testid={`${testId}-last`}><ChevronsRight className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );
}

function _getProjectAmount(p) {
  if (p?.project_amount) return p.project_amount;
  return (p?.one_time_charges || 0) + (p?.recurring_amount || 0) + (p?.cloud_server_charges || 0);
}

// ===== Reports Panel =====
function ReportsPanel({ projects, onSnapshot }) {
  const [activeReport, setActiveReport] = useState("outstanding");
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedProjCust, setExpandedProjCust] = useState("");
  const [activePreset, setActivePreset] = useState("current_fy");
  const [dateRange, setDateRange] = useState(() => {
    const r = getDatePreset("current_fy");
    return { start: r.start, end: r.end };
  });
  const [weeklyTargetWeeks, setWeeklyTargetWeeks] = useState(6);
  const [statusFilter, setStatusFilter] = useState("active");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);

  const applyPreset = (presetId) => {
    const r = getDatePreset(presetId);
    setActivePreset(presetId);
    setDateRange({ start: r.start, end: r.end });
  };

  const loadReport = useCallback(async (type) => {
    setLoading(true);
    try {
      let url = "";
      if (type === "revenue") url = `/collections/reports/revenue-by-stream?start_date=${dateRange.start}&end_date=${dateRange.end}`;
      else if (type === "efficiency") url = `/collections/reports/collection-efficiency?start_date=${dateRange.start}&end_date=${dateRange.end}`;
      else if (type === "outstanding") url = `/collections/reports/project-outstanding?page=${currentPage}&page_size=${pageSize}${statusFilter ? `&status=${statusFilter}` : ""}`;
      else if (type === "performance") url = `/collections/reports/performance?months=6`;
      else if (type === "project-customer") url = `/collections/reports/project-customer?min_customers=1${statusFilter ? `&status=${statusFilter}` : ""}`;
      else if (type === "weekly-targets") url = `/collections/reports/weekly-targets?weeks=${weeklyTargetWeeks}`;
      const res = await api.get(url);
      setReportData(res.data);
    } catch { toast.error("Failed to load report"); }
    setLoading(false);
  }, [dateRange, weeklyTargetWeeks, statusFilter, currentPage, pageSize]);

  useEffect(() => { loadReport(activeReport); }, [activeReport, loadReport]);

  const TABS = [
    { id: "weekly-targets", label: "Weekly Targets" },
    { id: "outstanding", label: "Project Outstanding" },
    { id: "project-customer", label: "Project × Customer" },
    { id: "revenue", label: "Revenue by Stream" },
    { id: "efficiency", label: "Efficiency" },
    { id: "performance", label: "Monthly" },
  ];

  // Reports that respect the From/To date range. Outstanding intentionally excluded —
  // it always shows real all-time outstanding (no period filter for accuracy, like Weekly Targets).
  const dateRangeApplies = ["revenue", "efficiency"].includes(activeReport);

  return (
    <div className="space-y-4" data-testid="reports-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Reports</h1>
        <div className="flex items-center gap-2">
          {dateRangeApplies && (
            <>
              <Input type="date" className="h-9 text-xs w-[130px]" value={dateRange.start} onChange={e => { setActivePreset(""); setDateRange(p => ({ ...p, start: e.target.value })); }} />
              <span className="text-xs text-slate-400">to</span>
              <Input type="date" className="h-9 text-xs w-[130px]" value={dateRange.end} onChange={e => { setActivePreset(""); setDateRange(p => ({ ...p, end: e.target.value })); }} />
            </>
          )}
          {activeReport === "outstanding" && (
            <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }} className="h-9 text-xs border border-slate-200 rounded px-2 bg-white" data-testid="report-status-filter">
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="on_hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>
          )}
          <Button data-testid="reports-refresh" variant="default" size="icon" onClick={() => loadReport(activeReport)} className="h-9 w-9 bg-[#0066CC] hover:bg-[#0052A3]" title="Refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>
      </div>
      {/* Date range presets — only when active report uses date range */}
      {dateRangeApplies && (
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="reports-date-presets">
        <span className="text-[11px] text-slate-500 font-medium pr-1">Quick:</span>
        {PRESETS.map(p => (
          <button key={p.id} data-testid={`preset-${p.id}`} onClick={() => applyPreset(p.id)}
            className={`px-2.5 py-1 text-[11px] rounded-full border transition ${activePreset === p.id ? "bg-[#0066CC] text-white border-[#0066CC]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>
            {p.label}
          </button>
        ))}
        {activePreset && <span className="text-[10px] text-slate-400 ml-1.5">{getDatePreset(activePreset).label}</span>}
      </div>
      )}
      <div className="flex gap-1.5 overflow-x-auto pb-1">
        {TABS.map(t => (<button key={t.id} onClick={() => { setReportData(null); setActiveReport(t.id); }} className={`px-3.5 py-2 rounded-lg text-xs font-medium whitespace-nowrap ${activeReport === t.id ? "bg-[#0066CC] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{t.label}</button>))}
      </div>
      {loading ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : reportData && (
        <>
          {activeReport === "weekly-targets" && reportData.weeks && (
            <div className="space-y-4" data-testid="weekly-targets-report">
              {/* Top: Last week vs Current week summary */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Last Week */}
                {reportData.last_week ? (
                  <Card className="border-slate-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Last Week</p>
                          <p className="text-sm font-medium text-slate-700">{reportData.last_week.label}</p>
                        </div>
                        <span className={`px-2 py-1 rounded text-[10px] font-bold ${reportData.last_week.achievement_pct >= 100 ? "bg-emerald-100 text-emerald-700" : reportData.last_week.achievement_pct >= 70 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                          {reportData.last_week.achievement_pct}% achieved
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div><p className="text-lg font-bold text-slate-800">{fmt(reportData.last_week.target)}</p><p className="text-[10px] text-slate-500">Target</p></div>
                        <div><p className="text-lg font-bold text-emerald-600">{fmt(reportData.last_week.collected)}</p><p className="text-[10px] text-slate-500">Collected · {reportData.last_week.receipts} receipt{reportData.last_week.receipts !== 1 ? "s" : ""}</p></div>
                        <div><p className={`text-lg font-bold ${reportData.last_week.gap > 0 ? "text-red-600" : "text-emerald-600"}`}>{reportData.last_week.gap > 0 ? `${fmt(reportData.last_week.gap)}` : `+${fmt(reportData.last_week.surplus)}`}</p><p className="text-[10px] text-slate-500">{reportData.last_week.gap > 0 ? "Shortfall" : "Surplus"}</p></div>
                      </div>
                      <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${reportData.last_week.achievement_pct >= 100 ? "bg-emerald-500" : reportData.last_week.achievement_pct >= 70 ? "bg-amber-400" : "bg-red-400"}`} style={{ width: `${Math.min(100, reportData.last_week.achievement_pct)}%` }} />
                      </div>
                    </CardContent>
                  </Card>
                ) : <Card className="border-slate-200"><CardContent className="p-4 text-center text-slate-400">No last-week data</CardContent></Card>}

                {/* Current Week */}
                {reportData.current_week && (
                  <Card className="border-2 border-[#0066CC]/30 bg-[#F0F7FF]">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-[10px] uppercase tracking-wider font-semibold text-[#0066CC]">Current Week</p>
                          <p className="text-sm font-medium text-slate-800">{reportData.current_week.label} <span className="text-[10px] text-slate-500">({reportData.days_remaining_in_week} day{reportData.days_remaining_in_week !== 1 ? "s" : ""} left)</span></p>
                        </div>
                        <span className={`px-2 py-1 rounded text-[10px] font-bold ${reportData.current_week.achievement_pct >= 100 ? "bg-emerald-100 text-emerald-700" : reportData.current_week.achievement_pct >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                          {reportData.current_week.achievement_pct}% MTD
                        </span>
                      </div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div><p className="text-lg font-bold text-slate-800">{fmt(reportData.current_week.target)}</p><p className="text-[10px] text-slate-500">Base Target</p></div>
                        {reportData.current_week.carry_forward > 0 && (
                          <div><p className="text-lg font-bold text-orange-600">+{fmt(reportData.current_week.carry_forward)}</p><p className="text-[10px] text-slate-500">Carry-forward</p></div>
                        )}
                        <div><p className="text-lg font-bold text-[#0066CC]">{fmt(reportData.current_week.effective_target)}</p><p className="text-[10px] text-slate-500">Effective Target</p></div>
                        <div><p className="text-lg font-bold text-emerald-600">{fmt(reportData.current_week.collected)}</p><p className="text-[10px] text-slate-500">Collected so far</p></div>
                      </div>
                      <div className="mt-3 h-2 bg-white rounded-full overflow-hidden border border-slate-200">
                        <div className={`h-full rounded-full ${reportData.current_week.achievement_pct >= 100 ? "bg-emerald-500" : "bg-[#0066CC]"}`} style={{ width: `${Math.min(100, reportData.current_week.collected / Math.max(1, reportData.current_week.effective_target) * 100)}%` }} />
                      </div>
                      {reportData.current_week.gap > 0 && reportData.days_remaining_in_week > 0 && (
                        <p className="text-[11px] text-slate-600 mt-2">
                          Need <span className="font-bold text-red-600">{fmt(Math.max(0, reportData.current_week.effective_target - reportData.current_week.collected))}</span> in next {reportData.days_remaining_in_week} day{reportData.days_remaining_in_week !== 1 ? "s" : ""} (~{fmt(Math.max(0, reportData.current_week.effective_target - reportData.current_week.collected) / Math.max(1, reportData.days_remaining_in_week))}/day)
                        </p>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Week selector */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500">Show last:</span>
                {[4, 6, 8, 12].map(n => (
                  <button key={n} onClick={() => setWeeklyTargetWeeks(n)} className={`px-2.5 py-1 text-xs rounded ${weeklyTargetWeeks === n ? "bg-[#0066CC] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{n} weeks</button>
                ))}
              </div>

              {/* Trend table */}
              <Card><CardContent className="p-4 overflow-x-auto">
                <table className="w-full text-xs" data-testid="weekly-targets-table">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 font-semibold text-slate-600">Week</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Base Target</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Carry-forward</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Effective Target</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Collected</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Gap / Surplus</th>
                      <th className="text-right py-2 px-2 font-semibold text-slate-600">Achievement</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.weeks.map(w => (
                      <tr key={w.week_start} className={`border-b border-slate-50 ${w.is_current ? "bg-blue-50/40" : ""}`}>
                        <td className="py-2.5 px-2 font-medium text-slate-800">{w.label} {w.is_current && <span className="ml-1 px-1.5 py-0.5 rounded bg-[#0066CC] text-white text-[9px] font-bold">NOW</span>}</td>
                        <td className="py-2.5 px-2 text-right text-slate-600">{fmt(w.target)}</td>
                        <td className="py-2.5 px-2 text-right text-orange-600 font-medium">{w.carry_forward > 0 ? `+${fmt(w.carry_forward)}` : "—"}</td>
                        <td className="py-2.5 px-2 text-right font-bold text-[#0066CC]">{fmt(w.effective_target)}</td>
                        <td className="py-2.5 px-2 text-right font-semibold text-emerald-700">{fmt(w.collected)}</td>
                        <td className={`py-2.5 px-2 text-right font-bold ${w.gap > 0 ? "text-red-600" : "text-emerald-600"}`}>{w.gap > 0 ? `-${fmt(w.gap)}` : `+${fmt(w.surplus)}`}</td>
                        <td className="py-2.5 px-2 text-right">
                          <div className="inline-flex items-center gap-1.5">
                            <div className="w-14 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${w.achievement_pct >= 100 ? "bg-emerald-500" : w.achievement_pct >= 70 ? "bg-amber-400" : "bg-red-400"}`} style={{ width: `${Math.min(100, w.achievement_pct)}%` }} />
                            </div>
                            <span className="text-[10px] font-bold w-9 text-right">{w.achievement_pct}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent></Card>

              {/* Carry-forward explanation */}
              <Card className="border-slate-200 bg-slate-50/60">
                <CardContent className="p-3 text-xs text-slate-600 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <p>
                    <span className="font-semibold text-slate-800">Auto-derived target:</span> Each week's base target = FY annual target ÷ 52.
                    <span className="font-semibold text-slate-800 ml-2">Carry-forward:</span> Any shortfall from a previous week is added to the next week's effective target until cleared. Surplus does not roll forward (it just shows as positive achievement that week).
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {activeReport === "outstanding" && reportData.projects && (() => {
            const pagination = reportData.pagination;
            const summaryProjects = pagination ? pagination.total_count : reportData.summary?.total_projects;
            const summaryValue = reportData.summary?.total_value || 0;
            const summaryCollected = reportData.summary?.total_collected || 0;
            const summaryOutstanding = reportData.summary?.total_outstanding || 0;
            return (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  ["Total Projects", summaryProjects, "text-slate-800"],
                  ["Total Value", fmt(summaryValue), "text-[#0066CC]"],
                  ["Collected", fmt(summaryCollected), "text-emerald-600"],
                  ["Outstanding", fmt(summaryOutstanding), "text-red-600"]
                ].map(([l, v, c]) => (
                  <div key={l} className="bg-white rounded-lg border p-3 text-center"><p className={`text-xl font-bold ${c}`}>{v}</p><p className="text-[10px] text-slate-500">{l}</p></div>
                ))}
              </div>
              <Card><CardContent className="p-4 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b">
                    <th className="text-left py-2 px-2 font-semibold text-slate-600">Project</th>
                    <th className="text-right py-2 px-2 font-semibold text-slate-600">Value</th>
                    <th className="text-right py-2 px-2 font-semibold text-slate-600">Collected</th>
                    <th className="text-right py-2 px-2 font-semibold text-slate-600">Outstanding</th>
                    <th className="text-right py-2 px-2 font-semibold text-slate-600">%</th>
                    <th className="text-center py-2 px-2 font-semibold text-slate-600">Snap</th>
                  </tr></thead>
                  <tbody>{reportData.projects.filter(p => !statusFilter || p.status === statusFilter || (!p.status && statusFilter === "active")).slice(0, pageSize).map(p => (
                    <tr key={p.id} className="border-b border-slate-50">
                      <td className="py-2.5 px-2"><span className="font-medium text-slate-800">{p.name}</span><span className={`ml-2 px-1.5 py-0.5 rounded text-[9px] font-medium ${p.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{p.status}</span></td>
                      <td className="py-2.5 px-2 text-right text-slate-700">{fmt(p.total_value)}</td>
                      <td className="py-2.5 px-2 text-right text-emerald-700 font-medium">{fmt(p.total_collected)}</td>
                      <td className="py-2.5 px-2 text-right font-bold text-red-600">{fmt(p.outstanding)}</td>
                      <td className="py-2.5 px-2 text-right"><div className="inline-flex items-center gap-1.5"><div className="w-12 h-1.5 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${p.collection_pct}%` }} /></div><span className="text-[10px] font-semibold">{p.collection_pct}%</span></div></td>
                      <td className="py-2.5 px-2 text-center"><button onClick={() => onSnapshot(p.id)} className="text-[#0066CC] hover:text-[#0052A3]"><Camera className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}</tbody>
                </table>
              </CardContent></Card>
              {/* Pagination Controls */}
              {(() => {
                const pagination = reportData.pagination || { page: currentPage, page_size: pageSize, total_count: reportData.summary?.total_projects || 0, total_pages: Math.max(1, Math.ceil((reportData.summary?.total_projects || 0) / pageSize)) };
                return pagination.total_pages > 1 && (
                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-slate-500">
                    Showing {((pagination.page - 1) * pagination.page_size) + 1}–{Math.min(pagination.page * pagination.page_size, pagination.total_count)} of {pagination.total_count} projects
                  </p>
                  <div className="flex items-center gap-1.5">
                    <Button variant="outline" size="sm" className="h-8 px-3 text-xs" disabled={currentPage <= 1} onClick={() => setCurrentPage(1)} data-testid="pagination-first">First</Button>
                    <Button variant="outline" size="sm" className="h-8 px-3 text-xs" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} data-testid="pagination-prev">Prev</Button>
                    <span className="text-xs font-medium text-slate-700 px-2">Page {pagination.page} of {pagination.total_pages}</span>
                    <Button variant="outline" size="sm" className="h-8 px-3 text-xs" disabled={currentPage >= pagination.total_pages} onClick={() => setCurrentPage(p => p + 1)} data-testid="pagination-next">Next</Button>
                    <Button variant="outline" size="sm" className="h-8 px-3 text-xs" disabled={currentPage >= pagination.total_pages} onClick={() => setCurrentPage(pagination.total_pages)} data-testid="pagination-last">Last</Button>
                  </div>
                </div>
              );})()}
            </div>
          );})()}
          {activeReport === "project-customer" && reportData?.projects && (() => {
            const data = reportData;
            return (
            <div className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                {[
                  ["Projects", data.summary?.total_projects, "text-slate-800"],
                  ["Project Value", fmt(data.summary?.total_value || 0), "text-[#0066CC]"],
                  ["Collected", fmt(data.summary?.total_collected || 0), "text-emerald-600"],
                  ["Outstanding", fmt(data.summary?.total_outstanding || 0), "text-red-600"],
                  [`${data.summary?.total_paid_customers || 0} / ${data.summary?.total_pending_customers || 0}`, null, "text-slate-800"],
                ].map(([l, v, c], i) => (
                  <div key={i} className="bg-white rounded-lg border p-3 text-center">
                    <p className={`text-xl font-bold ${c}`}>{v || l}</p>
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">{i === 4 ? "Paid · Pending Customers" : ["Projects", "Project Value", "Collected", "Outstanding"][i]}</p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500 italic">Tap a project row to expand its customers</p>
                <div className="flex gap-2">
                  <button onClick={() => setExpandedProjCust("all")} className="text-xs text-[#0066CC] hover:underline">Expand all</button>
                  <button onClick={() => setExpandedProjCust("")} className="text-xs text-slate-500 hover:underline">Collapse all</button>
                </div>
              </div>
              <div className="space-y-2">
                {data.projects.map(p => {
                  const isExpanded = expandedProjCust === "all" || expandedProjCust === p.project_id;
                  return (
                  <Card key={p.project_id} className="border-slate-200" data-testid={`project-customer-row-${p.project_id}`}>
                    <CardContent className="p-0">
                      <button onClick={() => setExpandedProjCust(prev => prev === p.project_id ? "" : p.project_id)} className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition">
                        <div className="flex items-center gap-3">
                          <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                          <div>
                            <p className="text-sm font-semibold text-slate-900">{p.project_name}</p>
                            <p className="text-[10px] text-slate-400 uppercase">{p.project_type}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6 text-right">
                          <div><p className="text-[10px] text-slate-400 uppercase">Value</p><p className="text-sm font-bold text-[#0066CC]">{fmt(p.total_value)}</p></div>
                          <div><p className="text-[10px] text-slate-400 uppercase">Collected</p><p className="text-sm font-bold text-emerald-600">{fmt(p.total_collected)}</p></div>
                          <div><p className="text-[10px] text-slate-400 uppercase">Outstanding</p><p className="text-sm font-bold text-red-600">{fmt(p.outstanding)}</p></div>
                          <div className="flex gap-1">
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700">{(p.paid_customers || []).length} paid</span>
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">{(p.pending_customers || []).length} pending</span>
                          </div>
                        </div>
                      </button>
                      {isExpanded && (
                        <div className="px-4 pb-4 pt-0 border-t border-slate-100 bg-slate-50/50">
                          <table className="w-full text-xs mt-3">
                            <thead>
                              <tr className="text-[10px] text-slate-500 uppercase tracking-wider">
                                <th className="text-left py-1.5 font-semibold">Customer</th>
                                <th className="text-center py-1.5 font-semibold">Receipts</th>
                                <th className="text-right py-1.5 font-semibold">Total Received</th>
                                <th className="text-right py-1.5 font-semibold">Last Receipt</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(p.paid_customers || []).map(c => (
                                <tr key={c.id} className="border-t border-slate-100" data-testid={`paid-customer-${c.id}`}>
                                  <td className="py-2 text-slate-800 font-medium">{c.name}</td>
                                  <td className="py-2 text-center text-slate-600">{c.receipts || 0}</td>
                                  <td className="py-2 text-right font-bold text-emerald-600">{fmt(c.amount)}</td>
                                  <td className="py-2 text-right text-slate-500">{c.last_receipt || "—"}</td>
                                </tr>
                              ))}
                              {(p.unmapped_receipt_total > 0 || p.unmapped_receipt_count > 0) && (
                                <tr className="border-t border-slate-100">
                                  <td className="py-2 text-slate-600">(Untagged) <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] bg-amber-100 text-amber-700 font-medium">untagged</span></td>
                                  <td className="py-2 text-center text-slate-600">{p.unmapped_receipt_count || 0}</td>
                                  <td className="py-2 text-right font-bold text-slate-700">{fmt(p.unmapped_receipt_total)}</td>
                                  <td className="py-2 text-right text-slate-500">{p.unmapped_last_receipt || "—"}</td>
                                </tr>
                              )}
                              {(p.pending_customers || []).length > 0 && (p.pending_customers || []).map(c => (
                                <tr key={c.id} className="border-t border-slate-100 opacity-60" data-testid={`pending-customer-${c.id}`}>
                                  <td className="py-2 text-slate-500">{c.name} <span className="ml-1 px-1.5 py-0.5 rounded text-[9px] bg-red-100 text-red-600 font-medium">pending</span></td>
                                  <td className="py-2 text-center text-slate-400">0</td>
                                  <td className="py-2 text-right text-slate-400">—</td>
                                  <td className="py-2 text-right text-slate-400">—</td>
                                </tr>
                              ))}
                              {(p.paid_customers || []).length === 0 && (p.unmapped_receipt_total || 0) === 0 && (p.pending_customers || []).length === 0 && (
                                <tr><td colSpan={4} className="py-3 text-center text-slate-400 italic">No customer data for this project.</td></tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            </div>
            );
          })()}
          {activeReport === "revenue" && reportData.streams && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {REVENUE_STREAMS.filter(s => !s.legacy || (reportData.streams[s.key] && reportData.streams[s.key].total_collected > 0)).map(s => { const d = reportData.streams[s.key]; return (
                <Card key={s.key} className={`${!d ? "opacity-50" : ""}`}><CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2"><div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} /><span className="text-xs font-semibold text-slate-700">{s.label}</span></div>
                  <p className="text-xl font-bold" style={{ color: d ? s.color : "#ccc" }}>{d ? fmt(d.total_collected) : "No data"}</p>
                </CardContent></Card>
              ); })}
            </div>
          )}
          {activeReport === "efficiency" && reportData.weeks && (
            <Card><CardContent className="p-4 overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b"><th className="text-left py-2 px-2">Week</th><th className="text-right py-2 px-2">Collected</th><th className="text-right py-2 px-2">Target</th><th className="text-right py-2 px-2">Efficiency</th></tr></thead>
                <tbody>{reportData.weeks.map(w => (
                  <tr key={w.week} className="border-b border-slate-50"><td className="py-2.5 px-2 font-medium">{new Date(w.week_start + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</td><td className="py-2.5 px-2 text-right font-semibold text-emerald-700">{fmt(w.collected)}</td><td className="py-2.5 px-2 text-right text-slate-500">{fmt(w.target)}</td><td className="py-2.5 px-2 text-right"><span className={`px-2 py-0.5 rounded font-bold ${w.efficiency >= 80 ? "bg-emerald-100 text-emerald-700" : w.efficiency >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{w.efficiency}%</span></td></tr>
                ))}</tbody>
              </table>
              {reportData.weeks.length === 0 && <p className="text-center text-slate-400 py-8">No data</p>}
            </CardContent></Card>
          )}
          {activeReport === "performance" && reportData.months && (
            <Card><CardContent className="p-4">
              <div className="space-y-3">{reportData.months.map(m => {
                const pct = Math.min(100, m.efficiency);
                return (<div key={m.month} className="flex items-center gap-3"><span className="text-xs font-medium text-slate-600 w-16 shrink-0">{m.month_name}</span><div className="flex-1"><div className="h-6 bg-slate-100 rounded-lg overflow-hidden relative"><div className={`h-full rounded-lg ${pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-400" : "bg-red-400"}`} style={{ width: `${pct}%` }} /><span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold">{m.efficiency}% ({fmt(m.collected)})</span></div></div></div>);
              })}</div>
            </CardContent></Card>
          )}
        </>
      )}
    </div>
  );
}


// ===== Customer Master Panel =====
function CustomerMasterPanel({ projects }) {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", business_type: "Others", project_id: "", industry: "", gstin: "",
    pan: "", contact_person: "", contact_email: "", contact_phone: "",
    status: "active", billing_address: "", city: "", state: "", pincode: "", notes: ""
  });

  const loadCustomers = async () => {
    setLoading(true);
    try { const r = await api.get("/collections/customers"); setCustomers(r.data || []); } catch {}
    setLoading(false);
  };

  useEffect(() => { loadCustomers(); }, []);

  const filtered = customers.filter(c => {
    if (statusFilter && c.status !== statusFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return c.name?.toLowerCase().includes(q) || c.gstin?.toLowerCase().includes(q) || c.contact_person?.toLowerCase().includes(q) || c.city?.toLowerCase().includes(q) || c.contact_email?.toLowerCase().includes(q);
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", business_type: "Others", project_id: "", industry: "", gstin: "", pan: "", contact_person: "", contact_email: "", contact_phone: "", status: "active", billing_address: "", city: "", state: "", pincode: "", notes: "" });
    setShowDialog(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name || "", business_type: c.business_type || "Project", project_id: c.project_id || "", industry: c.industry || "", gstin: c.gstin || "", pan: c.pan || "", contact_person: c.contact_person || "", contact_email: c.contact_email || "", contact_phone: c.contact_phone || "", status: c.status || "active", billing_address: c.billing_address || "", city: c.city || "", state: c.state || "", pincode: c.pincode || "", notes: c.notes || "" });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Customer name is required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/collections/customers/${editing.id}`, form);
        toast.success("Customer updated");
      } else {
        await api.post("/collections/customers", form);
        toast.success("Customer created");
      }
      setShowDialog(false);
      loadCustomers();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete customer "${name}"?`)) return;
    try { await api.delete(`/collections/customers/${id}`); toast.success("Deleted"); loadCustomers(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const getProjectName = (projectId) => {
    if (!projectId) return "";
    const p = projects.find(pr => pr.id === projectId);
    return p ? p.name : "";
  };

  const BUSINESS_TYPES = ["AMC", "Cloud", "Implementation", "Consultation", "Others"];

  return (
    <div className="space-y-4" data-testid="customer-master-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <Users className="w-5 h-5 text-[#0066CC]" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Customer Master</h1>
            <p className="text-xs text-slate-500">Central registry of all customer entities tagged to receipts & projects</p>
          </div>
        </div>
        <Button onClick={openCreate} className="bg-[#0066CC] hover:bg-[#0052A3] gap-2" data-testid="add-customer-btn"><Plus className="w-4 h-4" /> New Customer</Button>
      </div>

      {/* Search + Status Filter + Refresh */}
      <Card><CardContent className="p-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Search</Label>
            <div className="relative mt-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input className="pl-8 h-9 text-sm" placeholder="Name / GSTIN / contact / city" value={search} onChange={e => setSearch(e.target.value)} data-testid="customer-search" />
            </div>
          </div>
          <div className="w-[160px]">
            <Label className="text-[10px] text-slate-500 uppercase tracking-wider font-medium">Status</Label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="w-full h-9 mt-1 text-sm border border-slate-200 rounded px-2 bg-white">
              <option value="">All</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="pt-4">
            <Button variant="outline" size="icon" className="h-9 w-9" onClick={loadCustomers} title="Refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
          </div>
        </div>
      </CardContent></Card>

      {/* Table */}
      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
        <Card><CardContent className="p-0">
          <div className="px-4 py-3 border-b border-slate-100">
            <p className="text-sm font-semibold text-slate-700">Customers</p>
            <p className="text-[11px] text-slate-400">{filtered.length} of {customers.length} shown</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="customer-table">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left p-3 font-semibold text-slate-600">Name</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Business Type</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Project</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Contact</th>
                  <th className="text-center p-3 font-semibold text-slate-600">Receipts</th>
                  <th className="text-left p-3 font-semibold text-slate-600">Status</th>
                  <th className="text-center p-3 font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(c => (
                  <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                    <td className="p-3 font-medium text-slate-800">{c.name}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${c.business_type === "AMC" ? "bg-purple-100 text-purple-700" : c.business_type === "Cloud" ? "bg-sky-100 text-sky-700" : c.business_type === "Project" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`} data-testid={`customer-bt-pill-${c.id}`}>{c.business_type}</span></td>
                    <td className="p-3 text-slate-600 text-[11px]">{getProjectName(c.project_id) || "—"}</td>
                    <td className="p-3 text-slate-600">{c.contact_person || "—"}</td>
                    <td className="p-3 text-center font-medium text-[#0066CC]">{c.receipt_count || 0}</td>
                    <td className="p-3"><span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${c.status === "active" ? "text-emerald-600" : "text-slate-400"}`}>{c.status}</span></td>
                    <td className="p-3 text-center">
                      <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#0066CC]" title="Edit"><Edit2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => handleDelete(c.id, c.name)} className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 ml-1" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-400">No customers found.</td></tr>}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      )}

      {/* Customer Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit Customer" : "New Customer"}</DialogTitle>
            <DialogDescription>Customer details will be linked to receipts & projects across the Collection module.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs font-medium text-slate-600">Customer Name *</Label>
              <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Acme Industries Pvt Ltd" className="mt-1" data-testid="customer-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-600">Business Type</Label>
                <select value={form.business_type} onChange={e => setForm(p => ({...p, business_type: e.target.value}))} className="w-full h-9 mt-1 text-sm border border-slate-200 rounded px-2 bg-white" data-testid="customer-business-type">
                  {BUSINESS_TYPES.map(bt => <option key={bt} value={bt}>{bt}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Project</Label>
                <select value={form.project_id} onChange={e => setForm(p => ({...p, project_id: e.target.value}))} className="w-full h-9 mt-1 text-sm border border-slate-200 rounded px-2 bg-white" data-testid="customer-project">
                  <option value="">— Not tagged —</option>
                  {projects.filter(p => p.status === "active" || !p.status).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-600">Industry / Vertical</Label>
                <Input value={form.industry} onChange={e => setForm(p => ({...p, industry: e.target.value}))} placeholder="Manufacturing / Retail / ..." className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">GSTIN</Label>
                <Input value={form.gstin} onChange={e => setForm(p => ({...p, gstin: e.target.value}))} placeholder="33ABCDE1234F1Z5" className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-600">PAN</Label>
                <Input value={form.pan} onChange={e => setForm(p => ({...p, pan: e.target.value}))} placeholder="ABCDE1234F" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Contact Person</Label>
                <Input value={form.contact_person} onChange={e => setForm(p => ({...p, contact_person: e.target.value}))} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-600">Contact Email</Label>
                <Input value={form.contact_email} onChange={e => setForm(p => ({...p, contact_email: e.target.value}))} type="email" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Contact Phone</Label>
                <Input value={form.contact_phone} onChange={e => setForm(p => ({...p, contact_phone: e.target.value}))} className="mt-1" />
              </div>
            </div>
            <div className="w-1/2">
              <Label className="text-xs font-medium text-slate-600">Status</Label>
              <select value={form.status} onChange={e => setForm(p => ({...p, status: e.target.value}))} className="w-full h-9 mt-1 text-sm border border-slate-200 rounded px-2 bg-white">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Billing Address</Label>
              <textarea value={form.billing_address} onChange={e => setForm(p => ({...p, billing_address: e.target.value}))} className="w-full mt-1 text-sm border border-slate-200 rounded px-3 py-2 h-20 resize-y" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-medium text-slate-600">City</Label>
                <Input value={form.city} onChange={e => setForm(p => ({...p, city: e.target.value}))} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">State</Label>
                <Input value={form.state} onChange={e => setForm(p => ({...p, state: e.target.value}))} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-medium text-slate-600">Pincode</Label>
                <Input value={form.pincode} onChange={e => setForm(p => ({...p, pincode: e.target.value}))} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600">Notes</Label>
              <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} className="w-full mt-1 text-sm border border-slate-200 rounded px-3 py-2 h-20 resize-y" />
            </div>
          </div>
          <div className="flex items-center justify-center gap-3 pt-4 border-t mt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)} className="gap-1.5"><X className="w-3.5 h-3.5" /> Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#0066CC] hover:bg-[#0052A3] gap-1.5" data-testid="save-customer-btn">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {editing ? "Update Customer" : "Create Customer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
